<template>
  <div class="qr-login-container">
    <!-- 居中 -->
    <van-row type="flex" justify="center">
      <van-col span="22" class="main-content-wrapper">
        <div class="main-content">
          <!-- 标题和公告栏容器 -->
          <div class="site-header">
            <!-- 标题单独放置，使用div包裹使其居中 -->
            <div class="title-container">
              <h1 class="header-title" v-if="webSite.title">
                {{ webSite.title }}
              </h1>
            </div>

            <!-- 公告栏容器 -->
            <div class="header-container" :class="{'blur-effect': !flagQR && showTokenInput}">
              <!-- 自定义公告栏 -->
              <div class="custom-announcement">
                <div class="announcement-icon">
                  <van-icon name="volume-o"/>
                </div>
                <div class="announcement-content">
                  <!-- 添加min-height确保公告栏高度稳定 -->
                  <div class="announcement-placeholder">
                    <div v-if="noticeForm && noticeForm.length === 1" class="single-announcement">
                      <div v-html="noticeForm[0].value"></div>
                    </div>
                    <div v-else-if="!noticeForm || noticeForm.length === 0" class="single-announcement">
                      <div>加载中...</div>
                    </div>
                    <div v-else class="multiple-announcements">
                      <div class="announcement-list">
                        <div
                            v-for="(notice, index) in noticeForm"
                            :key="notice.key || index"
                            class="announcement-item"
                            v-show="index === currentNoticeIndex"
                        >
                          <div v-html="notice.value"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <canvas id="canvas2" hidden=""></canvas>

          <div class="token-card-container" v-show="!loadingQr"
               :class="{'blur-effect': !flagQR && showTokenInput, 'content-loaded': flagQR}">
            <div class="token-card">
              <div class="token-header">
                <span class="token-title">当前密钥</span>
                <div class="token-value clickable" @click="copy" title="点击复制密钥">
                  <span class="token-key">{{ token.token }}</span>
                  <span v-if="token.userName" class="token-username">({{ token.userName }})</span>
                </div>
                <van-tag round :type="tokenTypeIcon" class="token-status-tag">{{ tokenType }}</van-tag>
                <div class="token-message" v-if="tokenMsg">{{ tokenMsg }}</div>
              </div>

              <div v-if="(token.remainingTimes > 0 || token.usingCount === 0) && flagQR" class="token-info-container">
                <div class="token-info-flex">
                  <!-- 左侧信息区域 -->
                  <div class="token-info-left">
                    <div class="info-item">
                      <span class="info-label">开始时间</span>
                      <span class="info-value">{{ formatDate(token.beginDate) }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">结束时间</span>
                      <span class="info-value">{{ formatDate(token.endDate) }}</span>
                    </div>
                  </div>

                  <!-- 右侧信息区域 -->
                  <div class="token-info-right">
                    <div class="info-item">
                      <span class="info-label">百度账号</span>
                      <span class="info-value">{{ token.userName }}</span>
                      <van-tag round :type="userTypeIcon" class="status-tag-sm">{{ userType }}</van-tag>
                      <div v-if="userMsg" class="info-desc">{{ userMsg }}</div>
                    </div>
                    <div class="info-item">
                      <span class="info-label">剩余次数</span>
                      <span class="info-value">{{ token.remainingTimes }}</span>
                      <van-tag round type="warning" class="status-tag-sm">
                        {{ token.type === "day" ? '今日剩余' : '总共' }}
                      </van-tag>
                    </div>
                  </div>
                </div>

                <!-- 使用次数为0时的重新输入密钥按钮 -->
                <div v-if="token.remainingTimes <= 0" class="reenter-token-section">
                  <div class="reenter-token-message">
                    <van-icon name="warning-o" color="#ff976a" size="16"/>
                    <span>当前密钥使用次数为0，您可以重新输入其他密钥或者等待次数刷新</span>
                  </div>
                  <van-button type="primary" size="small" @click="showReenterToken" class="reenter-token-btn">
                    重新输入密钥
                  </van-button>
                </div>

                <!-- 账号分配状态提示和自动分配按钮 -->
                <div v-if="token.groupType != 0" class="account-assign-section">
                  <div class="account-assign-message">
                    <van-icon name="info-o" color="#1989fa" size="16"/>
                    <span>{{ token.groupMsg || '账号需要重新分配' }}</span>
                  </div>
                  <van-button type="primary" size="small" @click="autoAssignAccount" class="auto-assign-btn" :loading="assignLoading">
                    {{ assignLoading ? '分配中...' : '自动分配账号' }}
                  </van-button>
                </div>

                <!-- 登录方式1：账号密码登录 -->
                <div class="login-section" v-if="token.baiduPassword && token.remainingTimes > 0 && token.groupType != -1">
                  <div class="section-title">
                    <van-icon name="user-o"/>
                    <span>账号密码登录</span>
                    <a href="https://www.yuque.com/hezuvip/kb/oaezshbk90przzbd?singleDoc#" target="_blank"
                       class="tutorial-link">
                      <van-icon name="question-o"/>
                      <span>查看教程</span>
                    </a>
                  </div>
                  <!-- 账号密码复制框 -->
                  <div class="account-password-box">
                    <div class="account-password-content">
                      <div class="account-item">
                        <span class="label">账号：</span>
                        <span class="value clickable" @click="copyText(token.userName)"
                              title="点击复制">{{ token.userName }}</span>
                        <van-button size="small" type="primary" @click="copyText(token.userName)">复制</van-button>
                      </div>
                      <div class="password-item">
                        <span class="label">密码：</span>
                        <span class="value clickable" @click="copyText(token.baiduPassword)" title="点击复制">
                          <template v-if="showPassword">{{ token.baiduPassword }}</template>
                          <template v-else>••••••••</template>
                        </span>
                        <van-button size="small" icon="eye-o" plain @click="togglePasswordVisibility"
                                    style="margin-right: 4px;">
                          {{ showPassword ? '隐藏' : '显示' }}
                        </van-button>
                        <van-button size="small" type="primary" @click="copyText(token.baiduPassword)">复制</van-button>
                      </div>
                      <div class="verification-code-item">

                        <van-icon name="phone-o" class="phone-icon"/>
                        <span class="label">验证码：</span>
                        <span class="value clickable"
                              :class="{'has-code': phoneCode, 'no-code': !phoneCode}"
                              @click="phoneCode ? copyText(phoneCode) : getTelCode()"
                              title="点击复制验证码">
                          {{ phoneCode || '点击获取' }}
                        </span>
                        <van-button
                            size="small"
                            type="primary"
                            :loading="loadingCode"
                            :disabled="loadingCode || countdown > 0"
                            @click="getTelCode"
                        >
                          {{ countdown > 0 ? `${countdown}s` : '获取验证码' }}
                        </van-button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 登录方式2：扫码登录 -->
                <div class="login-section" v-if="!token.baiduPassword && token.remainingTimes > 0 && token.groupType != -1">
                  <div class="section-title">
                    <van-icon name="qr"/>
                    <span v-if="token.baiduPassword">扫码登录</span>
                    <span v-else>扫码登录</span>
                  </div>
                  <!-- 登录选项按钮 -->
                  <div class="login-options-bar">
                    <div class="login-option" @click="scanQrcode">
                      <van-icon name="scan" class="login-icon"/>
                      <span>扫码登录</span>
                      <span class="login-desc">电脑/平板/网页登录</span>
                      <div v-if="loadingQr" class="login-tip">
                        <van-icon name="info-o"/>
                        <span>请使用手机浏览器访问卡密页面，才能唤醒相机</span>
                      </div>
                      <a v-if="!loadingQr" :href="webSite.qrUrl" target="_blank" class="help-link" @click.stop>
                        <van-icon name="question-o"/>
                        <span>查看教程</span>
                      </a>
                    </div>
                    <label class="login-option">
                      <van-icon name="photo-o" class="login-icon"/>
                      <span>上传二维码</span>
                      <span class="login-desc">手机端app登录</span>
                      <input @click="showTos1($event)" @change="ocrQrcodeCode($event)" id="upload-input"
                             style="position:absolute;width:100%;height:100%;left:0;top:0;opacity:0;cursor:pointer;"
                             type="file" accept="image/*">
                      <a :href="webSite.updateUrl" target="_blank" class="help-link" @click.stop>
                        <van-icon name="question-o"/>
                        <span>查看教程</span>
                      </a>
                    </label>
                  </div>
                </div>

                <!-- 操作按钮行 -->
                <div v-if="token.remainingTimes > 0 && token.groupType != -1" class="token-action-bar">
                  <!-- PC端布局：三个按钮在一行 -->
                  <div class="pc-buttons">
                    <div class="action-btn" @click="showRemoveDevice">
                      <van-icon name="delete-o" class="action-icon"/>
                      <span>移除设备</span>
                      <span class="action-desc">{{
                          formatSeconds(token.expireTime)
                        }}内剩余{{ token.removeUsingCount || 0 }}次</span>
                    </div>
                    <div class="action-btn" @click="getEmail">
                      <van-icon name="envelop-o" class="action-icon"/>
                      <span>自助删文件</span>
                      <span class="action-desc">获取邮箱验证码</span>
                    </div>
                    <div class="action-btn" @click="copy">
                      <van-icon name="share-o" class="action-icon"/>
                      <span>复制链接</span>
                      <span class="action-desc">免输密钥链接</span>
                    </div>
                  </div>

                  <!-- 移动端布局：上面两个一行，下面一个单独一行 -->
                  <div class="mobile-buttons">
                    <!-- 上方按钮行：移除设备和获取验证码 -->
                    <div class="action-btn-row">
                      <div class="action-btn" @click="showRemoveDevice">
                        <van-icon name="delete-o" class="action-icon"/>
                        <span>移除设备</span>
                        <span class="action-desc">{{
                            formatSeconds(token.expireTime)
                          }}内剩余{{ token.removeUsingCount || 0 }}次</span>
                      </div>
                      <div class="action-btn" @click="getEmail">
                        <van-icon name="envelop-o" class="action-icon"/>
                        <span>自助删文件</span>
                        <span class="action-desc">获取邮箱验证码</span>
                      </div>
                    </div>

                    <!-- 下方按钮：复制链接 -->
                    <div class="action-btn-copy" @click="copy">
                      <van-icon name="share-o" class="action-icon"/>
                      <span>复制链接</span>
                      <span class="action-desc">免输密钥链接</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div class="token-agreement" @click="showTos">
            <span class="agreement-title">登陆前须知</span>
            <div class="agreement-check">
              <span>已阅读并同意：</span>
              <van-checkbox v-model="showCheckBox" shape="square"></van-checkbox>
            </div>
          </div>

          <div class="loadingMessage" v-if="loadingQr">
            <span>{{ loadingQrMessage }}</span>
          </div>

          <canvas id="canvas2" hidden=""></canvas>

          <div style="height: 30px"></div>

        </div>
      </van-col>
    </van-row>

    <!-- 添加一个隐藏的元素来设置全局样式 -->
    <div class="global-style-container"></div>
    <!--服务条款-->
    <van-dialog :beforeClose="tos" v-model="show" title="登陆须知" cancel-button-text="拒绝"
                confirm-button-text="我同意"
                show-cancel-button show-confirm-button :lockScroll="true">

      <div class="regulations" v-html="webSite.regulations">
      </div>

    </van-dialog>

    <van-dialog v-model="questionShow" show-confirm-button :title="questionTitle" :lockScroll="true">
      <div class="regulations" v-html="questionHtml">
      </div>
    </van-dialog>


    <van-dialog v-model="showTokenInput" :beforeClose="tokenInput" cancel-button-text="购买密钥"
                confirm-button-text="确认" :cancel-button-color="'#a20aee'" :confirm-button-color="'#1989fa'"
                show-cancel-button
                show-confirm-button :lockScroll="true" class="token-input-dialog" @confirm="confirmTokenInput">
      <template #title>
        <div class="dialog-title-with-icon">
          <van-icon name="lock" class="dialog-title-icon"/>
          <span>请输入您的密钥以访问账号</span>
        </div>
      </template>
      <div class="input-wrapper">
        <div class="input-field-container">
          <van-field
              v-model="tokenInputValue"
              placeholder="请输入密钥"
              :clearable="false"
              :border="false"
              class="token-input-field"
              input-align="center"
              ref="tokenInputField"
              maxlength="40"
              type="text"
              @keypress.enter="confirmTokenInput"
          >
            <template #right-icon>
              <van-icon v-if="tokenInputValue" name="clear" class="clear-icon" @click="clearTokenInput"/>
            </template>
          </van-field>
        </div>
        <div class="input-underline"></div>
        <div class="input-hint">
          <van-icon name="info-o" size="12"/>
          <span>收藏输入密钥后的链接，可直接访问账号</span>
        </div>
      </div>
    </van-dialog>

    <van-dialog v-model="emailCodeShow" title="验证码" :show-confirm-button="false" :show-cancel-button="true"
                cancel-button-text="关闭" class="email-dialog">
      <div class="email-content">
        <van-cell-group inset>
          <van-cell center>
            <template #icon>
              <van-icon name="envelop-o" size="20" color="#1989fa" style="margin-right: 8px;"/>
            </template>
            <template #title>
              <span class="email-title">请选择邮箱验证码进行验证</span>
            </template>
          </van-cell>
          <van-field v-model="emailCode" center clearable label="验证码" placeholder="" :border="false"
                     input-align="center" readonly class="email-code-field">
            <template #input>
              <div class="code-value clickable" @click="copyText(emailCode)" title="点击复制验证码">
                {{ emailCode }}
              </div>
            </template>
            <template #button>
              <van-button type="primary" size="small" round @click="getEmailCode()">
                重新获取
              </van-button>
            </template>
          </van-field>
        </van-cell-group>
      </div>
    </van-dialog>

    <van-dialog v-model="isVerification" :beforeClose="verification" title="操作频繁" confirm-button-text="确 认"
                :cancel-button-color="'#a20aee'" :confirm-button-color="'#ee0a24'" show-confirm-button
                :lockScroll="true"
                style="text-align: center;">

      <van-image :src="verificationData" height="50" width="160">
        <template v-slot:error>加载失败</template>
      </van-image>

      <van-field v-model="verificationCode" center clearable label="验证码" placeholder="请输入验证码"
                 class="verification-code-input">
        <template #button>
          <van-button size="small" type="primary" @click="verification()">刷新验证码</van-button>
        </template>
      </van-field>

    </van-dialog>

    <!-- 浏览器扫码提示对话框 -->
    <van-dialog
        v-model="showBrowserScanTip"
        :show-cancel-button="true"
        cancel-button-text="继续扫码"
        :confirm-button-text="isMobileDevice ? '②微信扫码' : '微信扫码'"
        @confirm="showQrCode"
        @cancel="continueToScan"
        class="browser-scan-tip-dialog">
      <template #title>
        <div class="custom-dialog-title">
          <van-icon name="warning-o" color="#ff976a" size="20"/>
          <span>提示</span>
        </div>
      </template>
      <div class="browser-scan-tip-content">
        <div class="tip-message">
          <p>浏览器扫描二维码不稳定，易登录失败</p>
          <p>推荐复制链接前往微信扫码登录</p>
        </div>
        <div class="tip-actions">
          <van-button type="default" size="small" icon="link-o" @click="copyCurrentLink">
            <span v-if="isMobileDevice">①复制链接</span>
            <span v-else>复制链接</span>
          </van-button>
        </div>
      </div>
    </van-dialog>

    <!-- 网页二维码对话框 -->
    <van-dialog
        v-model="showPageQrCode"
        title="微信扫码访问"
        :show-cancel-button="false"
        confirm-button-text="关闭"
        class="page-qrcode-dialog">
      <div class="page-qrcode-content">
        <div class="qrcode-container" ref="qrcodeContainer">
          <!-- 二维码将在这里生成 -->
        </div>
        <div class="qrcode-tip">
          <p>请使用微信扫描上方二维码</p>
          <p>在微信中访问本页面</p>
        </div>
      </div>
    </van-dialog>

    <!-- 新增扫码对话框 -->
    <van-dialog v-model="showScanDialog" title="扫码登录"
                :show-cancel-button="true" cancel-button-text="取消" :show-confirm-button="false"
                close-on-click-overlay :lockScroll="true" class="scan-dialog" @closed="closeScanDialog">
      <div class="scan-container">
        <div class="scan-header">
          <span>请将百度网盘二维码对准摄像头</span>
        </div>
        <div class="scan-content">
          <div class="scan-frame">
            <canvas id="scan-canvas" class="scan-canvas"></canvas>
            <div class="scan-overlay">
              <div class="scan-corner top-left"></div>
              <div class="scan-corner top-right"></div>
              <div class="scan-corner bottom-left"></div>
              <div class="scan-corner bottom-right"></div>
              <div class="scan-line"></div>
            </div>
          </div>
          <div class="scan-tip">
            {{ loadingQrMessage }}
          </div>
        </div>
      </div>
    </van-dialog>

    <van-dialog :beforeClose="removeDevice" v-model="removeDeviceTab" title="选择要移除的设备" cancel-button-text="取消"
                confirm-button-text="确 认" :confirm-button-color="'#ee0a24'" show-cancel-button show-confirm-button
                :lockScroll="true">
      <div class="remove-options">
        <van-radio-group v-model="removeType">
          <div class="radio-row">
            <van-radio name="1" class="radio-item">
              <template #default>
                <div class="radio-content">
                  <van-image width="40" height="40" :src="require('@/assets/pc.png')"/>
                  <span class="radio-label">移除电脑设备</span>
                  <span class="radio-desc">不超过3台</span>
                </div>
              </template>
            </van-radio>
            <van-radio name="2" class="radio-item">
              <template #default>
                <div class="radio-content">
                  <van-image width="40" height="40" :src="require('@/assets/android.png')"/>
                  <span class="radio-label">移除手机设备</span>
                  <span class="radio-desc">不超过3台</span>
                </div>
              </template>
            </van-radio>
            <van-radio name="3" class="radio-item">
              <template #default>
                <div class="radio-content">
                  <van-image width="40" height="40" :src="require('@/assets/ipad.png')"/>
                  <span class="radio-label">移除iPad设备</span>
                  <span class="radio-desc">不超过2台</span>
                </div>
              </template>
            </van-radio>
          </div>
        </van-radio-group>
      </div>
    </van-dialog>

    <!-- 激活密钥确认对话框 -->
    <van-dialog v-model="showActivateDialog" title="密钥激活"
                :show-cancel-button="true" cancel-button-text="取消"
                confirm-button-text="激活" :confirm-button-color="'#1989fa'"
                show-confirm-button :lockScroll="true"
                @confirm="activateToken" @cancel="cancelActivate"
                class="activate-dialog">
      <div class="activate-content">
        <div class="activate-message">
          <van-icon name="warning-o" color="#ff976a" size="24"/>
          <p>您的密钥尚未激活，需要激活后才能使用。</p>
          <p>是否立即激活此密钥？</p>
          <p>⚠️注意：激活后开始计算时间</p>
        </div>
      </div>
    </van-dialog>

    <div id="float-icons" :class="{'blur-effect': !flagQR && showTokenInput}">
      <float-icons padding="10 10 50 10" scroller="float-icons" class="icons-warp">
        <!-- top-start -->
        <!-- top-end -->
        <van-popover placement="top-end" v-model="showPopover" trigger="click" :overlay="true">
          <template #default>
            <div style="width: 300px;">
              <van-cell v-for="item in questionItemForm" :key="item.key" @click="onSelect(item)" :title="item.title"
                        :icon="item.icon">
                <template #icon>
                  <van-image width="18" height="18" style="padding-top: 3px;padding-right: 9px" :src="item.icon"/>
                </template>
              </van-cell>
            </div>
          </template>

          <template #reference>
            <div class="float-icon-item animated">
              <div class="float-icon-circle">
                <img :src="require('@/assets/kf.png')" alt="" draggable="false">
              </div>
              <span>常见问题</span>
            </div>
          </template>
        </van-popover>

      </float-icons>
    </div>
  </div>
</template>

<script>

import {Toast, Dialog} from 'vant';
import {h} from 'vue';
import {RadioGroup, Radio} from 'vant';

import FloatIcons from '@/components/s-icons'

import jsQR from "jsqr";
import router from "@/router";
import Clipboard from 'clipboard';

export default {
  name: "Index",
  data() {
    return {
      // 1获取token 2登陆

      emailCode: '',
      phoneCode: '', // 新增手机验证码变量
      emailCodeShow: false,

      operate: 0, // 1 for getEmailCode, 2 for removeDevice, 3 for getTelCode
      isVerification: false,
      verificationData: '',
      verificationKey: '',
      verificationCode: '',
      baiduUrl: '',
      questionShow: false,
      questionTitle: '',
      questionHtml: '',
      wxConfigSuccess: false,
      // 显示输入密钥弹出框
      showTokenInput: false,
      tokenInputValue: '',
      // 客服
      showPopover: false,

      // 浏览器扫码提示
      showBrowserScanTip: false,
      showPageQrCode: false,
      isMobileDevice: false,
      isWeChatBrowser: false,
      qrcode: null,

      // 服务条款
      show: false,
      showCheckBox: false,
      qrLock: true,
      // 是否是在摄像头
      loadingQr: false,
      loadingQrMessage: "请对准二维码进行扫描",
      // token是否有值 flagQR 决定扫码界面是否隐藏，默认隐藏
      flagQR: false,
      token: {
        token: ''
      },
      removeDeviceTab: false,
      removeType: "2", // 默认选择当前设备

      // Icon primary  success danger warning
      tokenTypeIcon: "success",
      tokenType: "正常",
      tokenMsg: "",

      userTypeIcon: "success",
      userType: "正常",
      userMsg: "",

      // 公告数组信息
      noticeForm: [],
      // 自定义公告栏相关
      currentNoticeIndex: 0,
      noticeInterval: null,
      displayNotices: [],

      // 网站信息
      webSite: {},

      // 问题信息
      questionItemForm: [],
      loginLocation: '',
      loginIp: '',
      questionItem: '',
      // 是否是扫码二维码登陆
      flag: '',
      showDeviceHelp: false,
      loadingCode: false,
      countdown: 0,
      timer: null,
      lastGetCodeTime: 0, // 上次获取验证码的时间
      codeInterval: 30, // 验证码获取间隔时间（秒）
      telVerificationShow: false, // 手机验证码弹窗显示状态
      showPassword: false, // 密码显示/隐藏状态
      showScanDialog: false, // 新增扫码对话框
      videoStream: null, // 存储视频流引用，以便在关闭对话框时停止
      showActivateDialog: false, // 激活密钥对话框显示状态
      activating: false, // 激活中状态
      assignLoading: false, // 自动分配账号加载状态
    };
  },
  created() {
    // 先获取token信息，避免等待网站信息加载
    this.getTokenInfo()
    // 异步获取网站信息，不阻塞页面渲染
    this.$nextTick(() => {
      this.getWebInfo()
    })
    // 检测浏览器类型
    this.detectBrowserType();
    if (this.isWx()) {
      this.wxConfig();
    }
  },
  components: {
    'float-icons': FloatIcons
  },
  mounted() {
    // 启动公告轮播
    this.startNoticeRotation();
  },
  beforeDestroy() {
    // 清除公告轮播定时器
    this.stopNoticeRotation();
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  watch: {
    noticeForm: {
      handler(newVal) {
        if (newVal && newVal.length > 1) {
          this.updateDisplayNotices();
          this.startNoticeRotation();
        } else if (newVal && newVal.length === 1) {
          this.displayNotices = newVal;
        } else {
          this.displayNotices = [];
        }
      },
      immediate: true
    }
  },
  methods: {
    // 启动/停止公告轮播
    startNoticeRotation() {
      // 先停止现有轮播
      this.stopNoticeRotation();

      if (this.noticeForm && this.noticeForm.length > 1) {
        this.updateDisplayNotices();

        // 每3秒切换一次公告
        this.noticeInterval = setInterval(() => {
          this.currentNoticeIndex = (this.currentNoticeIndex + 1) % this.noticeForm.length;
        }, 3000);
      }
    },

    stopNoticeRotation() {
      if (this.noticeInterval) {
        clearInterval(this.noticeInterval);
        this.noticeInterval = null;
      }
    },

    // 更新显示的公告
    updateDisplayNotices() {
      if (this.noticeForm && this.noticeForm.length > 0) {
        // 设置当前索引，确保在范围内
        this.currentNoticeIndex = Math.min(this.currentNoticeIndex, this.noticeForm.length - 1);
        // 设置显示的公告
        this.displayNotices = this.noticeForm;
      } else {
        this.displayNotices = [];
      }
    },
    getEmail() {
      this.emailCode = ''
      this.phoneCode = '' // 重置手机验证码

      // 检查是否同意登录须知
      if (!this.showCheckBox) {
        Toast.fail('请先阅读并同意登录须知');
        this.show = true; // 显示登录须知对话框
        return;
      }

      this.emailCodeShow = true
      this.getEmailCode();
    },
    // 激活密钥
    activateToken() {
      if (this.activating) {
        return;
      }

      this.activating = true;
      const token = this.token.token || this.$route.params.pathMatch;
      console.log(token)
      if (!token) {
        this.activating = false;
        Toast.fail('密钥不能为空');
        return;
      }
      this.$axios.post("/baidu/token/activateGroupToken", {
            "token": token,
            "verCode": this.verificationCode,
            "verKey": this.verificationKey,
          }
      ).then(res => {
        this.activating = false;
        
        // 检查是否需要验证码
        if (res.data.data && res.data.data.verKey) {
          this.verificationData = res.data.data.verData;
          this.verificationKey = res.data.data.verKey;
          this.operate = 4; // 设置操作类型为激活密钥
          this.isVerification = true;
          this.showActivateDialog = false;
          Toast.fail('请输入验证码后重试激活');
          return;
        }
        
        if (res.data.code === 200) {
          Toast.success('密钥激活成功！');
          this.showActivateDialog = false;
          // 重置验证码相关状态
          this.verificationCode = '';
          this.verificationKey = '';
          this.verificationData = '';
          this.isVerification = false;
          // 重新获取token信息
          this.getTokenInfo();
        } else {
          Toast.fail(res.data.msg || '激活失败，请重试');
        }
      }).catch(error => {
        this.activating = false;
        console.error('激活密钥失败:', error);
        Toast.fail('激活失败，请检查网络连接');
      });
    },

    // 取消激活
    cancelActivate() {
      this.showActivateDialog = false;
      // 如果需要验证码，显示验证码对话框；否则显示密钥输入框
      if (this.verificationKey) {
        this.isVerification = true;
      } else {
        this.showTokenInput = true;
      }
    },

    // 重新输入密钥
    showReenterToken() {
      // this.flagQR = false;
      this.tokenInputValue = '';
      this.showTokenInput = true;
      // 重置密钥状态
      this.setTokenStatus('', '', '');
      this.setUserStatus('', '', '');
    },

    // 自动分配账号
    autoAssignAccount() {
      if (this.assignLoading) return;
      
      this.assignLoading = true;
      
      // 显示加载提示
      const loadingToast = Toast.loading({
        message: '正在分配账号...',
        forbidClick: true,
        duration: 0 // 持续显示直到手动关闭
      });
      
      // 根据当前groupType设置对应的用户状态
      this.updateUserStatusByGroupType();
      
      // 模拟后端请求地址，设置5分钟超时
      this.$axios.post('/baidu/token/autoAssignAccount', {
        token: this.token.token
      }, {
        timeout: 300000 // 5分钟超时时间
      }).then(res => {
        loadingToast.clear(); // 关闭加载提示
        if (res.data.code === 200) {
          Toast.success('账号分配成功！');
          // 重新获取token信息以更新状态
          this.getTokenInfo();
        } else {
          Toast.fail(res.data.msg || '分配失败，请重试');
          // 分配失败时恢复原状态
          this.updateUserStatusByGroupType();
        }
      }).catch(error => {
        loadingToast.clear(); // 关闭加载提示
        console.error('自动分配账号失败:', error);
        if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
          Toast.fail('请求超时，请稍后重试');
        } else {
          Toast.fail('分配失败，请检查网络连接');
        }
        // 分配失败时恢复原状态
        this.updateUserStatusByGroupType();
      }).finally(() => {
        this.assignLoading = false;
      });
    },

    // 根据groupType更新用户状态显示
    updateUserStatusByGroupType() {
      if (!this.token.groupType) return;
      
      switch (this.token.groupType) {
        case -1:
          this.setUserStatus("warning", "未分配", "账号未分配，需要分配初始账号");
          break;
        case 1:
          this.setUserStatus("danger", "已删除", "绑定账号已删除，需要重新分配账号");
          break;
        case 2:
          this.setUserStatus("danger", "已禁用", "绑定账号已禁用，需要重新分配账号");
          break;
        case 3:
          this.setUserStatus("warning", "已过期", "绑定账号已过期，需要重新分配账号");
          break;
        default:
          // groupType为0时表示正常状态，不需要特殊处理
          break;
      }
    },

    getEmailCode() {
      // this.emailCode = 988874
      this.token.token = this.$route.params.pathMatch;
      // 判定是否为空
      console.log(this.token.token)

      // 设置loading状态为true
      this.loadingCode = true;

      this.$axios.post("/baidu/token/getEmailCode",
          {
            "token": this.token.token,
            "verCode": this.verificationCode,
            "verKey": this.verificationKey,
          }
      ).then(res => {
        console.log(res)
        // 判定 res.data.data中是否存在 image 考虑 res.data.data 为null
        if (res.data.data && res.data.data.image !== undefined) {
          this.verificationCode = ''
          this.operate = 1  // 修改为1表示getEmailCode操作
          this.isVerification = true;
          this.verificationData = res.data.data.image
          this.verificationKey = res.data.data.key
          this.loadingCode = false; // 设置loading状态为false
          return;
        }
        if (res.data.data && res.data.data.no !== undefined) {
          if (res.data.data.no === "0" && res.data.data.emailCode) {
            console.log(res.data.data)
            this.emailCode = res.data.data.emailCode
            let text = this.emailCode
            this.$copyText(text).then((e) => {
              console.log('复制成功');
              // Toast.success();
              const toast = Toast.success({
                duration: 0, // 持续展示 toast
                forbidClick: true,
                message: '获取成功\n 已复制到剪切板',
              });
              let second = 3;
              const timer = setInterval(() => {
                second--;
                if (second) {
                  toast.message = '获取成功\n 已复制到剪切板';
                } else {
                  clearInterval(timer);
                  // 手动清除 Toast
                  Toast.clear();
                }
              }, 1000);
            }, (e) => {
              console.log('复制成功');
            });

            // // 设置倒计时
            // this.lastGetCodeTime = Math.floor(Date.now() / 1000);
            // this.countdown = this.codeInterval;

            // if (this.timer) {
            //   clearInterval(this.timer);
            // }
            // this.timer = setInterval(() => {
            //   this.countdown--;
            //   if (this.countdown <= 0) {
            //     clearInterval(this.timer);
            //     this.timer = null;
            //   }
            // }, 1000);
          } else {
            // 非正常数据状态判定
            Toast.fail({
              // 持续展示 toast
              // duration: 0,
              forbidClick: true,
              message: res.data.data.msg,
            });
            // primary  success danger warning
            if (res.data.data.no.indexOf(",-1") !== -1) {
              // 密钥错误
              this.tokenInputValue = this.token.token
              this.showTokenInput = true;
              this.tokenTypeIcon = "danger"
              this.tokenType = "异常"
              this.tokenMsg = res.data.data.msg
              this.loadingCode = false; // 设置loading状态为false
              return;
            }
            if (res.data.data.no.indexOf(",-2") !== -1) {
              // 密钥找不到绑定账号
              this.tokenTypeIcon = "danger"
              this.tokenType = "异常"
              this.tokenMsg = res.data.data.msg

              this.userTypeIcon = "danger"
              this.userType = "异常"
              this.userMsg = res.data.data.msg


              this.tokenInputValue = this.token.token
              this.showTokenInput = true;
            }
            if (res.data.data.no.indexOf(",1") !== -1 || res.data.data.no.indexOf(",2") !== -1
                || res.data.data.no.indexOf(",3") !== -1) {
              // 时间未到或未开始 使用次数为0 密钥禁用
              this.tokenTypeIcon = "warning"
              this.tokenType = "异常"
              this.tokenMsg = res.data.data.msg

              this.tokenInputValue = this.token.token
              this.showTokenInput = true;
            }
            if (res.data.data.no.indexOf(",4") !== -1) {
              // 账号禁用
              this.userTypeIcon = "danger"
              this.userType = "异常"
              this.userMsg = "账号已禁用"

              this.tokenInputValue = this.token.token
              this.showTokenInput = true;
            }
          }
        }
        this.loadingCode = false; // 设置loading状态为false
      }).catch(error => {
        console.error('获取验证码失败:', error);
        Toast.fail('获取验证码失败');
        this.loadingCode = false; // 请求失败也要设置loading状态为false
      });
    },
    //复制链接
    copy() {
      let text = window.location.href;
      this.$copyText(text).then((e) => {
        console.log('复制成功');
        // Toast.success();
        const toast = Toast.success({
          duration: 0, // 持续展示 toast
          forbidClick: true,
          message: '复制成功\n 收藏你的密钥链接，下次登陆无需重复输入密钥！',
        });
        let second = 3;
        const timer = setInterval(() => {
          second--;
          if (second) {
            toast.message = '复制成功 \n 收藏你的密钥链接，下次登陆无需重复输入密钥！';
          } else {
            clearInterval(timer);
            // 手动清除 Toast
            Toast.clear();
          }
        }, 1000);

      }, (e) => {
        console.log('复制成功');

      })
    },
    verification(action, done) {
      // 如果是刷新操作（无参数调用）或非确认操作
      if (!action || action !== 'confirm') {
        // 根据当前操作类型选择正确的接口刷新验证码
        let url;
        const params = {
          "token": this.token.token,
          "verCode": "",
          "verKey": this.verificationKey || "",
        };

        // 根据当前操作类型选择正确的接口
        switch (this.operate) {
          case 0: // 默认（findToken）
            url = "/baidu/token/findToken";
            break;
          case 1: // 邮箱验证码
            url = "/baidu/token/getEmailCode";
            break;
          case 2: // 移除设备或扫码登录
            // 检查是从哪个功能进入的
            if (this.baiduUrl) {
              url = "/baidu/token/qrLogin";
              params.loginUrl = this.baiduUrl;
            } else {
              url = "/baidu/token/removeDevice";
              params.deviceType = this.removeType;
            }
            break;
          case 3: // 手机验证码
            url = "/baidu/token/getTelCode";
            break;
          case 4: // 激活密钥
            url = "/baidu/token/activateGroupToken";
            break;
          default:
            url = "/baidu/token/findToken";
        }

        // 根据API类型选择GET或POST方法
        let request;
        if (url === "/baidu/token/findToken") {
          // 构造GET请求URL
          const queryParams = new URLSearchParams();
          queryParams.append("token", params.token);
          queryParams.append("verCode", params.verCode);
          queryParams.append("verKey", params.verKey);
          queryParams.append("addInfo", false);
          request = this.$axios.get(`${url}?${queryParams.toString()}`);
        } else if (url === "/baidu/token/removeDevice") {
          // 构造GET请求URL
          const queryParams = new URLSearchParams();
          queryParams.append("token", params.token);
          queryParams.append("verCode", params.verCode);
          queryParams.append("verKey", params.verKey);
          queryParams.append("deviceType", params.deviceType);
          request = this.$axios.get(`${url}?${queryParams.toString()}`);
        } else {
          // 其他接口使用POST
          request = this.$axios.post(url, params);
        }

        // 发送请求获取新的验证码图片
        request.then(res => {
          if (res.data.data && res.data.data.image) {
            this.verificationData = res.data.data.image;
            this.verificationKey = res.data.data.key;
          } else {
            Toast.fail('刷新验证码失败');
          }
        }).catch(error => {
          console.error('刷新验证码失败:', error);
          Toast.fail('刷新验证码失败');
        });

        // 如果只是刷新操作，到此结束
        if (!done) return;
      }

      // 如果是关闭操作（取消按钮）
      if (action === 'cancel' && done) {
        this.isVerification = false;
        done();
        return;
      }

      // 如果是确认操作
      if (action === 'confirm' && done) {
        // 如果未输入验证码，不允许关闭
        if (!this.verificationCode) {
          Toast.fail('请输入验证码');
          return;
        }
        // 执行验证码提交操作
        this.submitCode();
        done();
      }
    },
    // 检测浏览器类型
    detectBrowserType() {
      const ua = window.navigator.userAgent.toLowerCase();
      // 检测是否是微信浏览器
      this.isWeChatBrowser = ua.indexOf("micromessenger") !== -1;
      // 检测是否是移动设备
      this.isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
    },

    isWx() {
      return this.isWeChatBrowser;
    },

    // 复制当前链接
    copyCurrentLink() {
      const currentUrl = window.location.href;
      this.$copyText(currentUrl).then(() => {
        Toast.success('链接已复制到剪贴板');
      }).catch(() => {
        Toast.fail('复制失败');
      });
    },

    // 显示二维码
    showQrCode() {
      // 如果是移动设备，尝试打开微信
      if (this.isMobileDevice) {
        // 尝试打开微信
        window.location.href = "weixin://";

        // 设置一个定时器，如果无法打开微信，提示用户手动打开
        setTimeout(() => {
          Toast('请手动打开微信，并粘贴链接访问');
        }, 2000);
      } else {
        try {
          // 如果是PC设备，显示当前页面的二维码
          this.showBrowserScanTip = false;
          this.showPageQrCode = true;

          // 在下一个事件循环中生成二维码，确保DOM已经渲染
          this.$nextTick(() => {
            this.generateQRCode();
          });
        } catch (error) {
          console.error('显示二维码失败:', error);
          // 如果显示二维码失败，回退到复制链接
          this.copyCurrentLink();
          Toast('二维码生成失败，已复制链接到剪贴板');
        }
      }
    },

    // 生成二维码
    generateQRCode() {
      const container = this.$refs.qrcodeContainer;
      if (container) {
        // 清除容器内容
        container.innerHTML = '';

        // 获取当前页面URL
        const currentUrl = window.location.href;

        // 使用图片API生成二维码
        const qrCodeApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(currentUrl)}`;

        // 创建图片元素
        const img = document.createElement('img');
        img.src = qrCodeApiUrl;
        img.alt = '扫描二维码';
        img.style.width = '200px';
        img.style.height = '200px';
        img.style.display = 'block';
        img.style.margin = '0 auto';

        // 添加到容器
        container.appendChild(img);

        // 添加错误处理
        img.onerror = () => {
          container.innerHTML = `
            <div style="padding: 20px; text-align: center;">
              <p style="margin-bottom: 10px;">二维码生成失败</p>
              <p style="word-break: break-all; font-size: 12px; color: #666;">${currentUrl}</p>
              <button style="margin-top: 10px; padding: 5px 10px;" onclick="navigator.clipboard.writeText('${currentUrl}')">
                复制链接
              </button>
            </div>
          `;
        };
      }
    },

    // 继续使用浏览器扫码
    continueToScan() {
      // 关闭提示，打开扫码对话框
      this.showBrowserScanTip = false;
      this.showScanDialog = true;

      // 在下一个事件循环中初始化摄像头，确保对话框已经打开
      this.$nextTick(() => {
        this.initCamera();
      });
    },
    // 微信配置
    wxConfig() {
      let vueThis = this
      const url = encodeURIComponent(location.href.split("#")[0]); //获取当前页面路由
      this.$axios.get("/wxJsSign?url=" + url).then(res => {
        const a = res.data.data
        wx.config({
          debug: false, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
          appId: a.appId, // 必填，公众号的唯一标识
          timestamp: a.timestamp, // 必填，生成签名的时间戳
          nonceStr: a.nonceStr, // 必填，生成签名的随机串
          signature: a.signature, // 必填，签名
          jsApiList: ["scanQRCode"] // 必填，需要使用的JS接口列表
        });
      })
      wx.error(function (err) {
        // Toast("不支持此功能");
        // config信息验证失败会执行error函数，如签名过期导致验证失败，具体错误信息可以打开config的debug模式查看，也可以在返回的res参数中查看，对于SPA可以在这里更新签名。
      });
      wx.ready(function () {
        vueThis.wxConfigSuccess = true;
        // Toast("微信访问");
      });
    },

    // 点击客服按钮
    onSelect(action) {
      // Toast(action.title);
      if (action.type === 'link') {
        window.open(action.value, "target");
        // "https://work.weixin.qq.com/kfid/kfc92b24b2e8580c75c"
      } else {
        // Dialog.alert({
        //   title: action.title,
        //   message: action.value,
        // })
        this.questionShow = true
        this.questionTitle = action.title
        this.questionHtml = action.value
      }
    },
    // 获取网站信息
    getWebInfo() {
      this.$axios.get("/baidu/config/get").then(res => {
        this.webSite = res.data.data || {}

        try {
          // 处理公告
          this.processNotices();
          // 处理问题
          this.processQuestions();
        } catch (error) {
          console.error("解析数据出错:", error);
          this.resetDataOnError();
        }
      }).catch(error => {
        console.error("获取网站信息失败:", error);
        this.resetDataOnError();
      })
    },

    // 处理公告数据
    processNotices() {
      this.noticeForm = [];

      if (this.webSite.notice) {
        const notice = JSON.parse(this.webSite.notice);
        if (notice && typeof notice === 'object') {
          Object.keys(notice).forEach(key => {
            if (notice[key] && notice[key].value) {
              this.noticeForm.push({
                value: notice[key].value,
                key: notice[key].key || key
              });
            }
          });
        }
      }

      // 更新显示并启动轮播
      this.updateDisplayNotices();
      if (this.noticeForm.length > 1) {
        this.startNoticeRotation();
      }
    },

    // 处理问题数据
    processQuestions() {
      this.questionItemForm = [];

      if (this.webSite.question) {
        const question = JSON.parse(this.webSite.question);
        Object.keys(question).forEach(key => {
          this.questionItemForm.push({
            value: question[key].value,
            key: question[key].key,
            type: question[key].type,
            title: question[key].title,
            icon: question[key].icon,
          });
        });
        this.questionItemForm.reverse();
      }
    },

    // 重置数据（出错时）
    resetDataOnError() {
      this.noticeForm = [];
      this.displayNotices = [];
      this.questionItemForm = [];
    },

    // 获取 token 信息
    getTokenInfo() {
      this.token.token = this.$route.params.pathMatch;

      if (!this.token.token) {
        this.tokenInputValue = '';
        this.showTokenInput = true;
        return;
      }

      // 检查用户是否已同意条款
      this.checkUserAgreement();

      // 获取是否是第一次进入
      const addInfo = this.checkFirstVisit();

      // 发送请求获取token信息
      this.fetchTokenInfo(addInfo);
    },

    // 检查用户是否已同意条款
    checkUserAgreement() {
      const agree = localStorage.getItem("TokenAgreeFirstEntry-" + this.token.token);
      if (agree) {
        if (this.userTypeIcon === "success" && this.tokenTypeIcon === "success") {
          this.flagQR = true;
        }
        this.qrLock = false;
        this.showCheckBox = true;
      }
    },

    // 检查是否首次访问
    checkFirstVisit() {
      const item = localStorage.getItem("TokenFirstEntry-" + this.token.token);
      if (!item) {
        localStorage.setItem("TokenFirstEntry-" + this.token.token, this.token.token);
        return true;
      }
      return false;
    },

    // 获取token信息
    fetchTokenInfo(addInfo) {
      const params = new URLSearchParams({
        token: this.token.token,
        addInfo: addInfo,
        verCode: this.verificationCode,
        verKey: this.verificationKey
      });

      this.$axios.get(`/baidu/token/findToken?${params}`).then(res => {
        if (res.data.data.image !== undefined) {
          this.handleVerificationRequired(res.data.data, 0);
          return;
        }

        if (res.data.data.no !== undefined) {
          if (res.data.data.no === "0") {
            this.token = res.data.data.data;
            this.flagQR = true;
            // 重置状态，确保激活后状态正确更新
            this.setTokenStatus("success", "正常", "");
            // 根据groupType设置用户状态，如果groupType为0则设置为正常
            if (this.token.groupType === 0) {
              this.setUserStatus("success", "正常", "");
            } else {
              this.updateUserStatusByGroupType();
            }
            // 重置激活相关状态
            this.showActivateDialog = false;
          } else {
            this.handleTokenError(res.data.data);
          }
        }
      });
    },

    // 处理需要验证码的情况
    handleVerificationRequired(data, operationType) {
      this.verificationCode = '';
      this.operate = operationType;
      this.isVerification = true;
      this.verificationData = data.image;
      this.verificationKey = data.key;
    },

    // 处理token错误
    handleTokenError(data) {
      this.flagQR = false;

      Toast.fail({
        forbidClick: true,
        message: data.msg,
      });

      this.tokenInputValue = this.token.token;

      // 密钥错误
      if (data.no.indexOf(",-1") !== -1) {
        this.setTokenStatus("danger", "异常", data.msg);
        this.showTokenInput = true;
        return;
      }

      this.token = data.data || this.token;

      // 密钥找不到绑定账号
      if (data.no.indexOf(",-2") !== -1) {
        this.setTokenStatus("danger", "异常", data.msg);
        this.setUserStatus("danger", "异常", data.msg);
        this.showTokenInput = true;
      }

      // 密钥未激活
      if (data.no.indexOf(",-3") !== -1) {
        this.setTokenStatus("warning", "未激活", data.msg);
        this.showActivateDialog = true;
      }

      // 时间未到或未开始 密钥禁用
      if (data.no.indexOf(",1") !== -1 || data.no.indexOf(",3") !== -1) {
        this.setTokenStatus("warning", "异常", data.msg);
        this.showTokenInput = true;
      }

      // 使用次数为0 - 显示密钥信息但提供重新输入选项
      if (data.no.indexOf(",2") !== -1) {
        this.setTokenStatus("warning", "使用次数为0", data.msg);
        this.flagQR = true; // 允许显示密钥信息
      }

      // 账号禁用
      if (data.no.indexOf(",4") !== -1) {
        this.setUserStatus("danger", "异常", "账号已禁用");
        this.showTokenInput = true;
      }
    },

    // 设置token状态
    setTokenStatus(iconType, status, message) {
      this.tokenTypeIcon = iconType;
      this.tokenType = status;
      this.tokenMsg = message;
    },

    // 设置用户状态
    setUserStatus(iconType, status, message) {
      this.userTypeIcon = iconType;
      this.userType = status;
      this.userMsg = message;
    },
    // 扫码登陆后
    QrLogin(action, done) {
      const vueThis = this
      if (action === 'confirm') {
        // 登陆
        console.log("登陆url：" + this.url)

        this.$axios.post("/baidu/token/qrLogin",
            {
              "token": this.token.token,
              "loginUrl": this.baiduUrl,
              "verCode": this.verificationCode,
              "verKey": this.verificationKey,
            }
        ).then(res => {
          console.log(res)
          if (res.data.data.image !== undefined) {
            this.verificationCode = ''
            this.operate = 2  // 这里已经正确，为2表示QrLogin
            this.isVerification = true;
            this.verificationData = res.data.data.image
            this.verificationKey = res.data.data.key
            return;
          }

          function beforeClose(action2, done2) {
            if (vueThis.flag)
              vueThis.$router.go(0);
            done2();
          }

          if (res.data.data !== null) {
            if (res.data.data.no === "0") {
              Dialog.alert({
                title: '登陆成功',
                message: res.data.data.msg
                , beforeClose
              })
            } else {
              Dialog.alert({
                title: '出错了',
                message: res.data.data.msg
                , beforeClose
              })
              if (res.data.data.no === "-1" || res.data.data.no === "1" || res.data.data.no === "2"
                  || res.data.data.no === "3" || res.data.data.no === "4") {
                this.tokenInputValue = this.token.token
                this.showTokenInput = true;
              }
            }
          }
        }).catch(error => {
          function beforeClose(action3, done3) {
            if (vueThis.flag)
              vueThis.$router.go(0);
            done3();
          }

          Dialog.alert({
            title: '出错了',
            message: error.message
            , beforeClose
          })
        })
        done();
      } else {
        // 取消按钮
        done();
      }
      // 扫码登陆重新加载次数减少加上会出问题 todo
      // await this.getTokenInfo()
      // 重新加载界面
    },

    loadingQrMessageChange(loadingQrMessage, loadingQr) {
      this.loadingQrMessage = loadingQrMessage
      this.loadingQr = loadingQr
    },
    wxQrSuccess(code) {
      // alert(code)
      let vueThis = this
      let reg = /wappass.baidu.com\/wp\/\?qrlogin/;
      if (code.match(reg)) {
        this.baiduUrl = code

        function beforeClose(action, done) {
          vueThis.flag = true
          vueThis.QrLogin(action, done);
        }

        Dialog.confirm({
          title: '确认要登录吗？',
          message: '扫码成功，请确认操作',
          beforeClose
        });
      } else {
        Dialog.alert({
          title: '出错了',
          message: "识别失败，请检查是否是百度网盘二维码"
        });
      }
    },
    // 扫码
    scanQrcode() {
      if (this.qrLock) {
        this.show = true;
        return;
      }
      const vueThis = this
      // 判定是否微信，微信执行逻辑
      if (this.isWx() && this.wxConfigSuccess) {
        wx.scanQRCode({
          // 微信扫一扫接口
          desc: "scanQRCode desc",
          needResult: 1, // 默认为0，扫描结果由微信处理，1则直接返回扫描结果，
          scanType: ["qrCode"], // 可以指定扫二维码还是一维码，默认二者都有
          success(res) {
            setTimeout(function () {
              const code = res.resultStr; // 当needResult 为 1 时，扫码返回的结果
              vueThis.wxQrSuccess(code)
            }, 1000)
          },
          fail(res) {
            setTimeout(function () {
              Toast(res.errMsg);
            }, 1000)
          }
        });
        return;
      }

      // 如果不是微信浏览器，显示提示对话框
      if (!this.isWeChatBrowser) {
        this.showBrowserScanTip = true;
      } else {
        // 如果是微信浏览器，直接显示扫码对话框
        this.showScanDialog = true;
        this.loadingQrMessage = "请将百度网盘二维码对准摄像头";

        // 在下一个事件循环中初始化摄像头，确保对话框已经打开
        this.$nextTick(() => {
          this.initCamera();
        });
      }
    },

    // 初始化摄像头
    initCamera() {
      const vueThis = this;
      // 创建video 组件
      const video = document.createElement("video");
      const canvasElement = document.getElementById("scan-canvas");
      const canvas = canvasElement.getContext("2d");

      //扫码成功后画线
      function drawLine(begin, end, color) {
        canvas.beginPath();
        canvas.moveTo(begin.x, begin.y);
        canvas.lineTo(end.x, end.y);
        canvas.lineWidth = 4;
        canvas.strokeStyle = color;
        canvas.stroke();
      }

      var constraints = {
        video: {
          width: {min: 320, ideal: 640, max: 1280},
          height: {min: 240, ideal: 480, max: 720},
          facingMode: "environment"
        }
      };

      // 清除canvas
      if (canvasElement) {
        canvasElement.width = canvasElement.offsetWidth;
        canvasElement.height = canvasElement.offsetHeight;
        canvas.clearRect(0, 0, canvasElement.width, canvasElement.height);
      }

      // 存储视频流引用，以便在关闭对话框时停止
      this.videoStream = null;

      navigator.getUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia;
      if (navigator.mediaDevices) {
        // 调用摄像头获取数据
        navigator.mediaDevices.getUserMedia(constraints).then(function (stream) {
          vueThis.videoStream = stream;
          video.srcObject = stream;
          video.setAttribute("playsinline", true); // required to tell iOS safari we don't want fullscreen
          video.setAttribute('webkit-playsinline', true);
          video.play();
          requestAnimationFrame(tick);
        }).catch(function (err) {
          console.error("摄像头访问失败:", err);
          Toast.fail("摄像头访问失败，请确保已授予摄像头权限");
          vueThis.showScanDialog = false;
        });
      } else if (navigator.getUserMedia) {
        navigator.getUserMedia(constraints).then(function (stream) {
          vueThis.videoStream = stream;
          video.srcObject = stream;
          video.setAttribute("playsinline", true);
          video.setAttribute('webkit-playsinline', true);
          video.play();
          requestAnimationFrame(tick);
        }).catch(function (err) {
          console.error("摄像头访问失败:", err);
          Toast.fail("摄像头访问失败，请确保已授予摄像头权限");
          vueThis.showScanDialog = false;
        });
      } else {
        Dialog.alert({
          title: '出错了',
          message: "对不起：未识别到扫描设备！"
        });
        this.showScanDialog = false;
      }

      function tick() {
        if (!vueThis.showScanDialog) {
          // 如果对话框已关闭，停止处理
          return;
        }

        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          // 显示摄像头的video
          canvasElement.hidden = false;

          canvasElement.height = video.videoHeight;
          canvasElement.width = video.videoWidth;
          canvas.drawImage(video, 0, 0, canvasElement.width, canvasElement.height);
          const imageData = canvas.getImageData(0, 0, canvasElement.width, canvasElement.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: "dontInvert",
          });
          if (code) {
            console.log("识别成功" + code.data)
            drawLine(code.location.topLeftCorner, code.location.topRightCorner, "#FF3B58");
            drawLine(code.location.topRightCorner, code.location.bottomRightCorner, "#FF3B58");
            drawLine(code.location.bottomRightCorner, code.location.bottomLeftCorner, "#FF3B58");
            drawLine(code.location.bottomLeftCorner, code.location.topLeftCorner, "#FF3B58");
            // 识别成功
            let reg = /wappass.baidu.com\/wp\/\?qrlogin/;
            if (code.data.match(reg)) {
              vueThis.baiduUrl = code.data;
              // 关闭扫码对话框
              vueThis.showScanDialog = false;

              function beforeClose(action, done) {
                vueThis.flag = true;
                vueThis.QrLogin(action, done);
              }

              Dialog.confirm({
                title: '确认要登录吗？',
                message: '扫码成功，请确认操作',
                beforeClose
              });
            } else {
              Dialog.alert({
                title: '出错了',
                message: "识别失败，请检查是否是百度网盘二维码"
              });
              vueThis.showScanDialog = false;
            }
            return; // 识别成功后停止循环
          }
        }
        requestAnimationFrame(tick);
      }
    },

    // 关闭扫码对话框
    closeScanDialog() {
      // 停止视频流
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => {
          track.stop();
        });
        this.videoStream = null;
      }

      // 重置状态
      this.loadingQr = false;
      this.loadingQrMessage = "请对准二维码进行扫描";
    },
    // 上传图片
    ocrQrcodeCode(input) {
      if (this.qrLock) {
        this.show = true;
        return;
      }
      // console.log(this)
      const vueThis = this
      try {
        const file = input.target.files[0];
        const img = document.createElement('img')
        const reader = new FileReader();
        reader.onload = function (e) {
          img.src = e.target.result
          img.onload = function () {
            const canvasElement = document.getElementById("canvas2");
            const canvas = canvasElement.getContext("2d");

            canvasElement.width = img.width
            canvasElement.height = img.height
            canvas.drawImage(img, 0, 0, canvasElement.width, canvasElement.height);
            const imageData = canvas.getImageData(0, 0, canvasElement.width, canvasElement.height);
            const code = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: "dontInvert",
            });
            // 识别成功
            if (code != null)
              console.log("识别成功" + code.data)
            let reg = /wappass.baidu.com\/wp\/\?qrlogin/;
            if (code != null && code.data.match(reg)) {
              // Dialog.alert({
              //   title: '获取到链接',
              //   message: code.data
              // })
              // this. = true;
              // outputData.innerText = '扫码成功，请确认操作';
              vueThis.baiduUrl = code.data

              function beforeClose(action, done) {
                vueThis.flag = false
                vueThis.QrLogin(action, done);
              }

              Dialog.confirm({
                title: '确认要登录吗？',
                message: '扫码成功，请确认操作',
                beforeClose
              });
            } else {
              // outputData.innerText = '识别失败，请检查二维码是否是百度二维码';
              Dialog.alert({
                title: '出错了',
                message: "识别失败，请检查是否是百度网盘二维码"
              });
            }
          }
        }
        // 读取完成后触发 onload 事件
        reader.readAsDataURL(file)
        document.getElementById("upload-input").value = ''
      } catch (err) {
        //alert(1)
        Dialog.alert({
          title: '出错了',
          message: err.message
        })
      }
    },
    //end
    // 服务条款
    showTos1(e) {
      console.log("登陆须知" + this.showCheckBox)
      if (this.qrLock) {
        this.show = true;
        e.preventDefault();
      }
    },
    showTos() {
      console.log("登陆须知" + this.showCheckBox)
      this.show = true
    },
    tos(action, done) {
      if (action === "cancel") {
        // 不同意
        this.flagQR = false
        this.showCheckBox = false
        localStorage.removeItem("TokenAgreeFirstEntry-" + this.token.token);
      } else {
        localStorage.setItem("TokenAgreeFirstEntry-" + this.token.token, "同意");
        // 同意
        if (this.userTypeIcon === "success" && this.tokenTypeIcon === "success")
          this.flagQR = true
        this.qrLock = false
        this.showCheckBox = true

      }
      done()
    },
    tokenInput(action, done) {
      if (action === "cancel") {
        // 购买密钥
        console.log("购买密钥")
        window.location.href = this.webSite.buyUrl
        done()
      } else {
        // 确认输入
        console.log("输入密钥" + this.tokenInputValue)
        window.location.href = "/qrLogin/" + this.tokenInputValue
        // router.push({path: "/qrLogin/", params: {pathMatch: this.tokenInputValue}})
      }
      // done()
    },
    utf8ByteToUnicodeStr(utf8Bytes) {
      var unicodeStr = "";
      for (var pos = 0; pos < utf8Bytes.length;) {
        var flag = utf8Bytes[pos];
        var unicode = 0;
        if ((flag >>> 7) === 0) {
          unicodeStr += String.fromCharCode(utf8Bytes[pos]);
          pos += 1;

        } else if ((flag & 0xFC) === 0xFC) {
          unicode = (utf8Bytes[pos] & 0x3) << 30;
          unicode |= (utf8Bytes[pos + 1] & 0x3F) << 24;
          unicode |= (utf8Bytes[pos + 2] & 0x3F) << 18;
          unicode |= (utf8Bytes[pos + 3] & 0x3F) << 12;
          unicode |= (utf8Bytes[pos + 4] & 0x3F) << 6;
          unicode |= (utf8Bytes[pos + 5] & 0x3F);
          unicodeStr += String.fromCharCode(unicode);
          pos += 6;

        } else if ((flag & 0xF8) === 0xF8) {
          unicode = (utf8Bytes[pos] & 0x7) << 24;
          unicode |= (utf8Bytes[pos + 1] & 0x3F) << 18;
          unicode |= (utf8Bytes[pos + 2] & 0x3F) << 12;
          unicode |= (utf8Bytes[pos + 3] & 0x3F) << 6;
          unicode |= (utf8Bytes[pos + 4] & 0x3F);
          unicodeStr += String.fromCharCode(unicode);
          pos += 5;

        } else if ((flag & 0xF0) === 0xF0) {
          unicode = (utf8Bytes[pos] & 0xF) << 18;
          unicode |= (utf8Bytes[pos + 1] & 0x3F) << 12;
          unicode |= (utf8Bytes[pos + 2] & 0x3F) << 6;
          unicode |= (utf8Bytes[pos + 3] & 0x3F);
          unicodeStr += String.fromCharCode(unicode);
          pos += 4;

        } else if ((flag & 0xE0) === 0xE0) {
          unicode = (utf8Bytes[pos] & 0x1F) << 12;
          ;
          unicode |= (utf8Bytes[pos + 1] & 0x3F) << 6;
          unicode |= (utf8Bytes[pos + 2] & 0x3F);
          unicodeStr += String.fromCharCode(unicode);
          pos += 3;

        } else if ((flag & 0xC0) === 0xC0) { //110
          unicode = (utf8Bytes[pos] & 0x3F) << 6;
          unicode |= (utf8Bytes[pos + 1] & 0x3F);
          unicodeStr += String.fromCharCode(unicode);
          pos += 2;

        } else {
          unicodeStr += String.fromCharCode(utf8Bytes[pos]);
          pos += 1;
        }
      }
      return unicodeStr;
    },
    showRemoveDevice() {
      console.log("处理移除设备" + this.showCheckBox)
      // 判定是否有移除权限 没有则提示
      if (this.token.removeUsingCount === undefined || this.token.removeUsingCount === 0) {
        this.showDeviceHelp = true
      } else {
        this.removeDeviceTab = true
      }
    },
    removeDevice(action, done) {
      if (action === "cancel") {
        // 取消
        done()
      } else {
        const vueThis = this
        this.$axios.get("/baidu/token/removeDevice?token=" + this.token.token + "&deviceType=" + this.removeType
            + "&verCode=" + this.verificationCode
            + "&verKey=" + this.verificationKey
        ).then(res => {
          console.log(res)
          if (res.data.data.image !== undefined) {
            this.verificationCode = ''
            this.operate = 2
            this.isVerification = true;
            this.verificationData = res.data.data.image
            this.verificationKey = res.data.data.key
            return;
          }

          function beforeClose(action2, done2) {
            if (vueThis.flag)
              vueThis.$router.go(0);
            // 操作成功后才刷新token信息
            if (res.data.data && res.data.data.no === "0") {
              vueThis.getTokenInfo();
            }
            done2();
          }

          if (res.data.data !== null) {
            if (res.data.data.no === "0") {
              Dialog.alert({
                title: '操作成功',
                message: res.data.data.msg
                , beforeClose
              })
            } else {
              Dialog.alert({
                title: '出错了',
                message: res.data.data.msg
                , beforeClose
              })
              if (res.data.data.no === "-1" || res.data.data.no === "1" || res.data.data.no === "2"
                  || res.data.data.no === "3" || res.data.data.no === "4") {
                this.tokenInputValue = this.token.token
                this.showTokenInput = true;
              }
            }
          }
          done();
        }).catch(error => {
          function beforeClose(action3, done3) {
            if (vueThis.flag)
              vueThis.$router.go(0);
            done3();
          }

          Dialog.alert({
            title: '出错了',
            message: error.message
            , beforeClose
          })
          done();
        })
        // 移除这里的getTokenInfo调用，防止在操作完成前就调用findToken
        done();
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatSeconds(seconds) {
      if (!seconds) return '0小时';

      const days = Math.floor(seconds / (24 * 60 * 60));
      const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));

      if (days > 0) {
        return `${days}天`;
      } else {
        return `${hours}小时`;
      }
    },
    copyText(text) {
      this.$copyText(text).then(() => {
        Toast.success('复制成功');
      }).catch(() => {
        Toast.fail('复制失败');
      });
    },
    async getTelCode() {
      const now = Math.floor(Date.now() / 1000);
      const timeSinceLastGet = now - this.lastGetCodeTime;

      // 检查是否同意登录须知
      if (!this.showCheckBox) {
        Toast.fail('请先阅读并同意登录须知');
        this.show = true; // 显示登录须知对话框
        return;
      }

      if (this.loadingCode || this.countdown > 0) {
        Toast('请勿频繁发送验证码，会拦截。验证码5分钟内未收到可重试。正在获取中...');
        return;
      }

      if (timeSinceLastGet < this.codeInterval) {
        Toast.fail(`请等待${this.codeInterval - timeSinceLastGet}秒后再次获取`);
        return;
      }

      this.loadingCode = true;
      // 创建一个持久的 Toast，会一直显示直到手动清除
      const loadingToast = Toast.loading({
        duration: 0, // 持续时间为0表示不会自动关闭
        forbidClick: true, // 禁止背景点击
        message: '请勿频繁发送验证码，会拦截。验证码5分钟内未收到可重试。正在获取中...'
      });

      // 无论成功还是失败，都更新最后获取时间并启动倒计时
      this.lastGetCodeTime = now;
      this.startCountdown();

      try {
        // 获取token
        this.token.token = this.$route.params.pathMatch;

        // 使用POST方法和与getEmailCode相同的参数格式
        const res = await this.$axios.post("/baidu/token/getTelCode", {
          "token": this.token.token,
          "verCode": this.verificationCode,
          "verKey": this.verificationKey,
        });

        // 手动清除loading Toast
        Toast.clear();

        // 处理验证码图片逻辑（与getEmailCode一致）
        if (res.data.data && res.data.data.image !== undefined) {
          this.verificationCode = '';
          this.operate = 3; // 设置为手机验证码操作
          this.isVerification = true;
          this.verificationData = res.data.data.image;
          this.verificationKey = res.data.data.key;
          this.loadingCode = false;
          return;
        }

        if (res.data.data && res.data.data.no === "0" && res.data.data.emailCode) {
          this.phoneCode = res.data.data.emailCode; // 修改为使用phoneCode

          // 复制到剪贴板的逻辑
          let text = this.phoneCode; // 修改为使用phoneCode
          this.$copyText(text).then(() => {
            const toast = Toast.success({
              duration: 0,
              forbidClick: true,
              message: '获取成功\n 已复制到剪切板',
            });

            let second = 3;
            const timer = setInterval(() => {
              second--;
              if (second) {
                toast.message = '获取成功\n 已复制到剪切板';
              } else {
                clearInterval(timer);
                Toast.clear();
              }
            }, 1000);
          }).catch(() => {
            Toast.fail('复制失败');
          });
        } else {
          Toast.fail(res.data.data?.msg || '获取验证码失败');
        }
      } catch (error) {
        // 手动清除loading Toast
        Toast.clear();

        console.error('获取验证码失败:', error);
        Toast.fail('获取验证码失败');
      } finally {
        this.loadingCode = false;
      }
    },

    // 新增启动倒计时的方法
    startCountdown() {
      this.countdown = this.codeInterval;

      if (this.timer) {
        clearInterval(this.timer);
      }

      this.timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }, 1000);
    },

    // 提交验证码后继续获取手机验证码
    submitTelVerification() {
      if (!this.verificationCode) {
        Toast.fail('请输入验证码');
        return;
      }

      this.telVerificationShow = false;
      this.getTelCode(); // 重新调用获取验证码方法
    },
    /* 验证码提交 */
    submitCode() {
      this.isVerification = false;

      // 保存当前验证码和密钥，以便在下一次请求中使用
      const currentVerCode = this.verificationCode;
      const currentVerKey = this.verificationKey;

      // 清空输入验证码，准备下一次输入
      this.verificationCode = '';

      // 根据操作类型处理不同的验证码提交
      const operationHandlers = {
        0: () => this.handleFindTokenVerification(currentVerCode, currentVerKey),
        1: () => this.handleEmailCodeVerification(currentVerCode, currentVerKey),
        2: () => this.handleDeviceOrQrVerification(currentVerCode, currentVerKey),
        3: () => this.handleTelCodeVerification(currentVerCode, currentVerKey),
        4: () => this.handleActivateTokenVerification(currentVerCode, currentVerKey),
        default: () => this.getTokenInfo()
      };

      // 执行对应的处理函数
      (operationHandlers[this.operate] || operationHandlers.default)();
    },

    // 处理findToken验证码
    handleFindTokenVerification(verCode, verKey) {
      const params = {
        token: this.token.token,
        addInfo: false,
        verCode: verCode,
        verKey: verKey
      };

      this.$axios.get("/baidu/token/findToken", {params}).then(res => {
        if (res.data.data && res.data.data.image !== undefined) {
          this.handleVerificationRequired(res.data.data, 0);
          return;
        }

        if (res.data.data && res.data.data.no !== undefined) {
          if (res.data.data.no === "0") {
            this.token = res.data.data.data;
            this.flagQR = true;
          } else {
            Toast.fail(res.data.data.msg || '操作失败');
          }
        }
      }).catch(this.handleApiError);
    },

    // 处理邮箱验证码
    handleEmailCodeVerification(verCode, verKey) {
      const params = {
        token: this.token.token,
        verCode: verCode,
        verKey: verKey
      };

      this.$axios.post("/baidu/token/getEmailCode", params).then(res => {
        if (res.data.data && res.data.data.image !== undefined) {
          this.handleVerificationRequired(res.data.data, 1);
          return;
        }

        if (res.data.data && res.data.data.no === "0" && res.data.data.emailCode) {
          this.emailCode = res.data.data.emailCode;
          this.copyToClipboardWithToast(this.emailCode);
        } else {
          Toast.fail(res.data.data?.msg || '获取验证码失败');
        }
      }).catch(error => {
        console.error('获取验证码失败:', error);
        Toast.fail('获取验证码失败');
      });
    },

    // 处理设备移除或扫码登录验证码
    handleDeviceOrQrVerification(verCode, verKey) {
      if (this.baiduUrl) {
        this.handleQrLoginVerification(verCode, verKey);
      } else {
        this.handleRemoveDeviceVerification(verCode, verKey);
      }
    },

    // 处理扫码登录验证码
    handleQrLoginVerification(verCode, verKey) {
      const params = {
        token: this.token.token,
        loginUrl: this.baiduUrl,
        verCode: verCode,
        verKey: verKey
      };

      this.$axios.post("/baidu/token/qrLogin", params).then(res => {
        if (res.data.data && res.data.data.image !== undefined) {
          this.handleVerificationRequired(res.data.data, 2);
          return;
        }

        if (res.data.data && res.data.data.no !== undefined) {
          Dialog.alert({
            title: res.data.data.no === "0" ? '登录成功' : '出错了',
            message: res.data.data.msg
          });
        }
      }).catch(this.handleApiError);
    },

    // 处理移除设备验证码
    handleRemoveDeviceVerification(verCode, verKey) {
      const params = {
        token: this.token.token,
        deviceType: this.removeType,
        verCode: verCode,
        verKey: verKey
      };

      this.$axios.get("/baidu/token/removeDevice", {params}).then(res => {
        if (res.data.data && res.data.data.image !== undefined) {
          this.handleVerificationRequired(res.data.data, 2);
          return;
        }

        if (res.data.data && res.data.data.no !== undefined) {
          Dialog.alert({
            title: res.data.data.no === "0" ? '操作成功' : '出错了',
            message: res.data.data.msg
          });

          if (res.data.data.no === "0") {
            this.getTokenInfo(); // 刷新信息
          }
        }
      }).catch(this.handleApiError);
    },

    // 处理手机验证码
    handleTelCodeVerification(verCode, verKey) {
      const params = {
        token: this.token.token,
        verCode: verCode,
        verKey: verKey
      };

      this.$axios.post("/baidu/token/getTelCode", params).then(res => {
        if (res.data.data && res.data.data.image !== undefined) {
          this.handleVerificationRequired(res.data.data, 3);
          return;
        }

        if (res.data.data && res.data.data.no === "0" && res.data.data.emailCode) {
          this.phoneCode = res.data.data.emailCode;
          this.lastGetCodeTime = Math.floor(Date.now() / 1000);
          this.countdown = this.codeInterval;

          this.copyToClipboardWithToast(this.phoneCode);
          this.startCountdown();
        } else {
          Toast.fail(res.data.data?.msg || '获取验证码失败');
        }
      }).catch(error => {
        console.error('获取验证码失败:', error);
        Toast.fail('获取验证码失败');
      });
    },

    // 处理激活密钥验证码
    handleActivateTokenVerification(verCode, verKey) {
      const params = {
        token: this.token.token,
        verCode: verCode,
        verKey: verKey
      };

      this.$axios.post("/baidu/token/activateGroupToken", params).then(res => {
        if (res.data.data && res.data.data.verKey) {
          this.handleVerificationRequired(res.data.data, 4);
          return;
        }

        if (res.data.code === 200) {
          Toast.success('密钥激活成功！');
          // 重置验证码相关状态
          this.verificationCode = '';
          this.verificationKey = '';
          this.verificationData = '';
          this.isVerification = false;
          // 重新获取token信息
          this.getTokenInfo();
        } else {
          Toast.fail(res.data.msg || '激活失败，请重试');
        }
      }).catch(error => {
        console.error('激活密钥失败:', error);
        Toast.fail('激活失败，请检查网络连接');
      });
    },

    // 复制到剪贴板并显示提示
    copyToClipboardWithToast(text) {
      this.$copyText(text).then(() => {
        const toast = Toast.success({
          duration: 0,
          forbidClick: true,
          message: '获取成功\n 已复制到剪切板',
        });

        let second = 3;
        const timer = setInterval(() => {
          second--;
          if (second) {
            toast.message = '获取成功\n 已复制到剪切板';
          } else {
            clearInterval(timer);
            Toast.clear();
          }
        }, 1000);
      }).catch(() => {
        Toast.fail('复制失败');
      });
    },

    // 启动倒计时
    startCountdown() {
      if (this.timer) {
        clearInterval(this.timer);
      }

      this.timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }, 1000);
    },

    // 处理API错误
    handleApiError(error) {
      console.error('操作失败:', error);
      Toast.fail('操作失败');
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    clearTokenInput() {
      this.tokenInputValue = '';
      if (this.$refs.tokenInputField) {
        this.$refs.tokenInputField.focus();
      }
    },

    confirmTokenInput() {
      if (this.tokenInputValue.trim()) {
        window.location.href = "/qrLogin/" + this.tokenInputValue;
      }
    },
    resetData() {
      // 重置所有数据
      this.token = {
        // ... existing fields ...
      };
      this.loadingCode = false;
      this.countdown = 0;
      this.emailCode = '';
      this.phoneCode = ''; // 重置手机验证码
      this.lastGetCodeTime = 0;
      // ... existing code ...
    },

    // 检测iOS设备
    isIOS() {
      const ua = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(ua);
    },


  }
}
</script>


<style lang="less" scoped>
// 变量定义
@primary-color: #1989fa;
@primary-dark: #096dd9;
@primary-light: #ecf5ff;
@danger-color: #ee0a24;
@warning-color: #ed6a0c;
@text-color: #303133;
@text-secondary: #606266;
@text-light: #909399;
@border-color: #ebeef5;
@bg-light: #f5f7fa;
@box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
@border-radius: 8px;
@transition-default: all 0.3s ease;

// 基础样式
canvas {
  pointer-events: none;
}

// 浮动图标样式
.icons-warp {
  .float-icon-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 44px;
    height: 44px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    z-index: 1001;
    user-select: none;
    -webkit-user-drag: none;

    &.animated {
      transition: @transition-default;

      &:hover {
        transform: scale(1.05);
      }
    }

    .float-icon-circle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      background: linear-gradient(135deg, @primary-color, @primary-dark);
      border-radius: 50%;
      box-shadow: 0 3px 8px rgba(25, 137, 250, 0.3);
      margin-bottom: 0;
      transition: @transition-default;
      position: relative;
      overflow: hidden;

      &:before {
        content: '';
        position: absolute;
        top: -10px;
        left: -10px;
        right: -10px;
        bottom: -10px;
        background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
        transform: rotate(45deg);
        animation: shine 3s infinite;
      }

      &:hover {
        box-shadow: 0 5px 12px rgba(25, 137, 250, 0.5);
        transform: translateY(-2px);
      }
    }

    img {
      width: 24px;
      height: 24px;
      filter: brightness(10);
      user-select: none;
      -webkit-user-drag: none;
      pointer-events: none;
    }

    span {
      display: none;
    }
  }
}

// 设备右侧样式
.device-right {
  flex-direction: column;
  justify-content: center;
  align-items: center;

  span {
    display: flex;
  }
}

// 动画定义
@keyframes shine {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  50%, 100% {
    transform: translateX(100%) rotate(45deg);
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateY(-15px);
  }
  70% {
    opacity: 0.7;
    transform: translateY(3px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

// 规则条款样式
.regulations {
  height: 500px;
  overflow: auto;
  word-wrap: break-word;
  word-break: normal;
  margin: 20px 10px 20px 20px;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none;
  }

  /* Chrome, Safari, Opera */
}

// 头部容器样式
.header-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
  background: linear-gradient(to right, #f6f8fc, #edf2f7);
  border-radius: @border-radius + 4;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: visible;
  transition: @transition-default;
  animation: fadeIn 2s ease-in-out;
}

.header-title {
  margin: 20px 0 5px 0;
  color: #409EFF;
  font-size: 24px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: center;
  transition: @transition-default;
  animation: fadeIn 2.3s ease-in-out;
}

// 自定义通知栏样式
.custom-notice-bar {
  flex: 1;
  min-width: 0;
  padding: 0 !important;
  height: auto !important;
  min-height: 40px;
  line-height: normal !important;

  :deep(.van-notice-bar__wrap) {
    height: 100%;
  }

  :deep(.van-notice-bar__content) {
    height: 100%;
    align-items: center;
    display: flex;
  }

  :deep(.van-notice-bar__left-icon) {
    margin-right: 6px;
    font-size: 16px;
    color: @warning-color;
  }
}

// 通知相关样式
.notice {
  &-content {
    height: 40px;
    overflow: hidden;
    width: 100%;
  }

  &-swipe {
    height: 40px;
    width: 100%;
  }

  &-item {
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 1.4;
    height: 40px;
    width: 100%;
  }
}

// 加载消息
.loadingMessage {
  color: #d73636;
  text-align: center;
}

// 扫码和上传区域样式
.saoma, .shangc {
  outline: none;
  border-radius: 10px;
  box-shadow: 1px 5px 1px 1px #ddd;
  font-size: 16px;
  width: 95%;
  text-align: center;
  margin: 10px auto;
  border-right: 1px solid #ededed;
  border-bottom: 1px solid #ededed;
  border-left: 1px solid #ededed;
  padding: 20px 0;
}

.saoma {
  height: 157px;
  border-top: 50px solid #d4edda;
}

.shangc {
  height: 150px;
  border-top: 50px solid #d1ecf1;
  position: relative;
}

// 模块样式
.mokuai img {
  width: 100px;
  height: 100px;
}

// 文本样式和链接
.ts, .ts-a {
  padding: 10px;
  text-align: center;
}

.ts-a a:hover {
  color: #409EFF;
}

.ts a {
  background-color: #409EFF;
  color: #ffffff;
  padding: 8px;
  border-radius: 5px;

  &:hover {
    background-color: rgb(61, 122, 236);
    color: #000102;
  }
}

// 标题和图标样式
.custom-title {
  margin-right: 4px;
  vertical-align: middle;
}

.search-icon {
  font-size: 16px;
  line-height: inherit;
}

// 帮助内容样式
.device-help-content {
  padding: 8px;

  p {
    margin: 0 0 8px 0;
    color: #666;
  }

  .help-link {
    color: @primary-color;
    text-decoration: none;
    display: block;

    &:hover {
      text-decoration: underline;
    }
  }
}

// 邮箱对话框样式
.email-dialog {
  :deep(.van-dialog__header) {
    padding: 20px 0;
    font-weight: bold;
    font-size: 18px;
  }

  .email-content {
    padding: 16px;

    .email-title {
      font-size: 14px;
      color: #323233;
    }

    :deep(.van-cell-group--inset) {
      margin: 0;
    }

    :deep(.van-field__button) {
      .van-button {
        height: 32px;
        padding: 0 16px;
      }
    }
  }
}

// 激活密钥对话框样式
.activate-dialog {
  .activate-content {
    padding: 20px 16px;
    text-align: center;
  }

  .activate-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
      color: #323233;

      &:first-of-type {
        font-weight: 500;
      }

      &:last-of-type {
        color: #969799;
      }
    }
  }
}

// 移除选项样式
.remove-options {
  padding: 20px;

  .radio-row {
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .radio-item {
    flex: 0 0 48%;
    padding: 12px;
    border: 1px solid #ebedf0;
    border-radius: @border-radius;

    &:deep(.van-radio__icon) {
      display: none;
    }

    &:deep(.van-radio__label) {
      margin: 0;
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }

  .radio-label {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
  }

  .radio-desc {
    font-size: 12px;
    color: #999;
  }

  .verification-code {
    text-align: center;
    padding: 20px;
  }

  .code {
    font-size: 24px;
    font-weight: bold;
    color: @primary-color;
  }

  :deep(.van-radio--checked) {
    .radio-item {
      border-color: @danger-color;
      background-color: #fff1f1;
    }
  }
}

// 密钥卡片样式
.token-card-container {
  padding: 0 12px;
  margin-bottom: 20px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0s, transform 0s;

  &.content-loaded {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 1s ease-out, transform 1s ease-out;
  }
}

.token-card {
  background: #ffffff;
  border-radius: @border-radius + 4;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: @transition-default;
}

// 卡片头部样式
.token-header {
  background: linear-gradient(135deg, @primary-color, @primary-dark);
  color: white;
  padding: 16px;
  position: relative;
  border-radius: @border-radius + 4 @border-radius + 4 0 0;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    right: -50%;
    bottom: -50%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: rotate(45deg);
    animation: headerShine 4s infinite;
    z-index: 1;
    pointer-events: none;
  }

  // 头部内容样式
  .token-title, .token-value, .token-message {
    position: relative;
    z-index: 2;
  }

  .token-title {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
    display: block;
  }

  .token-value {
    font-size: 18px;
    font-weight: bold;
    word-break: break-all;
    margin-bottom: 8px;
    font-family: monospace;
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    .token-key {
      margin-right: 6px;
    }
  }

  .token-status-tag {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
  }

  .token-message {
    font-size: 12px;
    opacity: 0.9;
  }
}

@keyframes headerShine {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  50%, 100% {
    transform: translateX(100%) rotate(45deg);
  }
}

@keyframes fadeInContent {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

// 卡片信息容器
.token-info-container {
  padding: 12px 16px;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeInContent 1s forwards;
  animation-delay: 0.2s;
}

.token-info-flex {
  display: flex;
  border-bottom: 1px solid #f2f2f2;
  padding: 12px 0;
  opacity: 0;
  animation: fadeInContent 1s forwards;
  animation-delay: 0.3s;
}

.token-info-left {
  flex: 1;
  border-right: 1px solid @border-color;
  padding-right: 16px;
}

.token-info-right {
  flex: 1;
  padding-left: 16px;
}

// 信息项样式
.info-item {
  margin-bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  &:last-child {
    margin-bottom: 0;
  }

  .info-label {
    font-size: 13px;
    color: @text-light;
    margin-right: 8px;
    min-width: 65px;
  }

  .info-value {
    font-size: 15px;
    font-weight: 500;
    color: @text-color;
    margin-right: 8px;
  }

  .info-desc {
    font-size: 12px;
    color: @text-light;
    width: 100%;
    margin-top: 2px;
    padding-left: 65px; /* 与label对齐 */
  }
}

.status-tag-sm {
  height: 20px;
  font-size: 12px;
}

// 操作按钮栏
.token-action-bar {
  display: flex;
  justify-content: space-between;
  padding: 12px 0 8px;
  margin-top: 16px;
  border-top: 1px solid @border-color;
  background-color: #fafafa;
  border-radius: 0 0 @border-radius @border-radius;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeInContent 1s forwards;
  animation-delay: 0.5s;
}

// 操作按钮样式
.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  padding: 12px 8px;
  border-radius: @border-radius;
  cursor: pointer;
  transition: @transition-default;
  margin: 0 6px;
  height: 100px;
  background-color: #f8f9fa;
  box-shadow: @box-shadow;

  &:hover {
    background-color: @primary-light;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .action-icon {
    font-size: 24px;
    color: @primary-color;
    margin-bottom: 8px;
  }

  span {
    font-size: 14px;
    font-weight: normal;
    color: @text-color;
  }

  .action-desc {
    width: 100%;
    min-height: 14px; /* 确保即使没有内容也占据空间 */
    font-size: 12px;
    color: @text-light;
    margin-top: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// 协议同意区域
.token-agreement {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid @border-color;
  background: @bg-light;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeInContent 1s forwards;
  animation-delay: 0.8s;

  .agreement-title {
    color: @primary-color;
    font-size: 14px;
  }

  .agreement-check {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: @text-secondary;
  }
}

// 登录选项样式
.login-options-bar {
  display: flex;
  justify-content: space-between;
  padding: 12px 0 8px;
  margin-top: 16px;
  border-top: 1px dashed @border-color;
}

.login-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  padding: 12px 4px;
  border-radius: @border-radius;
  cursor: pointer;
  transition: @transition-default;
  position: relative;
  background-color: #f8f9fa;
  margin: 0 8px;

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    background-color: @primary-light;
    transform: translateY(-2px);
    box-shadow: @box-shadow;
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: @text-color;
  }

  .login-desc {
    width: 100%;
    font-size: 12px;
    color: @text-light;
    margin-top: 4px;
  }
}

.login-icon {
  font-size: 24px;
  color: @primary-color;
  margin-bottom: 6px;
}

// 登录提示和帮助链接
.login-tip {
  position: absolute;
  bottom: -5px;
  left: 0;
  right: 0;
  font-size: 12px;
  color: #f56c6c;
  margin-top: 4px;
  background-color: rgba(255, 229, 229, 0.8);
  padding: 2px 0;
  border-radius: 0 0 @border-radius @border-radius;
}

.help-link {
  position: absolute;
  bottom: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  font-size: 12px;
  color: @primary-color;
  text-decoration: none;
  z-index: 10;
  padding: 6px;

  &:hover {
    color: #0056b3;
    text-decoration: underline;
  }

  .van-icon {
    font-size: 14px;
    margin-right: 2px;
  }
}

/* 移动端适配 */
@media screen and (max-width: 767px) {
  // 卡片内容适配
  .token-value {
    flex-direction: column;
    align-items: flex-start;

    .token-key {
      margin-right: 0;
      margin-bottom: 4px;
      width: 100%;
    }

    .token-username {
      margin-left: 0;
      align-self: flex-start;
    }
  }

  // 信息布局适配
  .token-info-flex {
    flex-direction: column;
  }

  .token-info-left {
    border-right: none;
    border-bottom: 1px solid @border-color;
    padding-right: 0;
    padding-bottom: 12px;
    margin-bottom: 12px;
  }

  .token-info-right {
    padding-left: 0;
  }

  // 登录选项适配
  .login-options-bar {
    flex-direction: column;
    gap: 12px;
  }

  .login-option {
    margin: 0;
    padding: 16px 4px;
  }

  // 操作按钮布局适配
  .token-action-bar {
    display: block;
    padding: 0;

    .pc-buttons {
      display: none;
    }

    .mobile-buttons {
      display: block;

      // 上排按钮行
      .action-btn-row {
        display: flex;
        justify-content: space-between;
        padding: 12px 0 8px;

        .action-btn {
          flex: 1;
          margin: 0 4px;
          height: 90px;
        }
      }

      // 下排复制按钮
      .action-btn-copy {
        flex: 0 0 100%;
        max-width: 100%;
        margin: 0;
        border-top: 1px dashed @border-color;
        padding: 15px 4px 12px;
        background-color: #f8fafd;
        border-radius: 0 0 @border-radius @border-radius;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        cursor: pointer;
        transition: @transition-default;
        height: 90px;

        .action-icon {
          font-size: 24px;
          color: @primary-color;
          margin-bottom: 8px;
        }

        > span:nth-child(2) {
          font-size: 14px;
          font-weight: normal;
          color: @text-color;
        }
      }
    }
  }

  // 操作描述文本适配
  .action-btn .action-desc,
  .action-btn-copy .action-desc {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    max-width: 100%;
    width: 100%;
    font-size: 12px;
    color: @text-light;
    margin-top: 6px;
    min-height: 14px;
  }

  // 头部容器适配
  .header-container {
    flex-direction: column;
    align-items: flex-start;
    padding: 10px;
    margin-top: 15px;
  }

  .header-title {
    font-size: 18px;
    margin-bottom: 8px;
  }

  .custom-announcement {
    width: 100%;
    padding: 6px 8px;
  }

  .announcement-icon {
    padding-top: 0;
    margin-top: 0;
    margin-right: 8px;
  }
}

// PC端和移动端布局控制
.pc-buttons {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.mobile-buttons {
  display: none;

  .action-btn {
    flex: 1;
    margin: 0 4px;
  }

  .action-btn-copy {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 15px 4px 12px;
    border-radius: 4px;
    cursor: pointer;
    transition: @transition-default;
    margin: 0;
    background-color: #f8fafd;

    &:hover {
      background-color: @primary-light;
    }

    .action-icon {
      font-size: 24px;
      color: @primary-color;
      margin-bottom: 8px;
    }

    span {
      font-size: 15px;
      font-weight: normal;
      color: @text-color;
    }

    .action-desc {
      width: 100%;
      font-size: 11px;
      color: @text-light;
      margin-top: 2px;
    }
  }
}

// 自定义公告栏样式
.custom-announcement {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  border-radius: @border-radius;
  overflow: visible;
  padding: 8px;
  transition: @transition-default;
  opacity: 1;
}

.announcement {
  &-icon {
    margin-right: 10px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    align-self: center;

    .van-icon {
      font-size: 18px;
      color: @warning-color;
    }
  }

  &-content {
    flex: 1;
    min-width: 0;
    overflow: visible;
  }

  &-placeholder {
    min-height: 40px;
    display: flex;
    align-items: center;
  }

  &-item {
    position: relative;
    width: 100%;
    color: @warning-color;
    font-size: 14px;
    line-height: 1.5;
    white-space: normal;
    overflow: visible;
    padding: 0;
    word-break: break-word;
  }

  &-list {
    position: relative;
  }
}

.single-announcement {
  white-space: normal;
  color: @warning-color;
  font-size: 14px;
  line-height: 1.5;
  padding: 0;
  overflow: visible;
  word-break: break-word;
  width: 100%;
}

.multiple-announcements {
  position: relative;
  overflow: visible;
}

// 过渡动画
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* 移动端适配 - 额外样式 */
@media screen and (max-width: 767px) {
  .header-container {
    padding: 10px;
    transition: @transition-default;
  }

  .header-title {
    font-size: 18px;
    margin: 15px 0 5px 0;
    text-align: center;
    transition: @transition-default;
  }

  .custom-announcement {
    width: 100%;
    padding: 6px 8px;
    transition: @transition-default;
  }

  .announcement-icon {
    padding-top: 0;
    margin-top: 0;
    margin-right: 8px;
  }
}

/* 账号密码复制框样式 */
.account-password {
  &-box {
    margin: 16px 0;
    padding: 12px;
    background-color: #f8f9fa;
    border-radius: @border-radius;
    border: 1px solid @border-color;
  }

  &-title {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    color: @primary-color;
    font-size: 14px;
    font-weight: 500;

    .van-icon {
      margin-right: 6px;
      font-size: 16px;
    }
  }

  &-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

// 账号密码项共享样式
.account-item,
.password-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #ffffff;
  border-radius: 6px;
  border: 1px solid @border-color;

  .label {
    color: @text-secondary;
    font-size: 14px;
    min-width: 50px;
  }

  .value {
    flex: 1;
    color: @text-color;
    font-size: 14px;
    margin-right: 12px;

    &.clickable {
      cursor: pointer;
      color: #666;
      padding: 3px 6px;
      background-color: @bg-light;
      border-radius: 4px;
      transition: all 0.2s;
      user-select: all;

      &:hover {
        background-color: @primary-light;
        color: #409eff;
      }
    }
  }

  .van-button {
    flex-shrink: 0;
    height: 28px;
    padding: 0 12px;
  }
}

/* 移动端适配 - 账号密码框 */
@media screen and (max-width: 767px) {
  .account-password-box {
    margin: 12px 0;
    padding: 10px;
  }

  .account-item,
  .password-item {
    padding: 6px 10px;

    .label {
      font-size: 13px;
    }

    .value {
      font-size: 13px;
    }

    .van-button {
      height: 26px;
      padding: 0 10px;
    }
  }
}

/* 重新输入密钥按钮样式 */
.reenter-token-section {
  margin: 16px 0;
  padding: 12px 16px;
  background-color: #fff9e6;
  border: 1px solid #ffd666;
  border-radius: @border-radius;
  text-align: center;

  .reenter-token-message {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    color: #d48806;
    font-size: 14px;

    .van-icon {
      margin-right: 6px;
    }
  }

  .reenter-token-btn {
    background-color: #1989fa;
    border-color: #1989fa;
    color: white;
    padding: 8px 20px;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.3s;

    &:hover {
      background-color: #0c7cd5;
      border-color: #0c7cd5;
    }
  }
}

/* 移动端适配 - 重新输入密钥按钮 */
@media screen and (max-width: 767px) {
  .reenter-token-section {
    margin: 12px 0;
    padding: 10px 12px;

    .reenter-token-message {
      font-size: 13px;
      margin-bottom: 10px;
    }

    .reenter-token-btn {
      padding: 6px 16px;
      font-size: 13px;
    }
  }
}

/* 自动分配账号按钮样式 */
.account-assign-section {
  margin: 16px 0;
  padding: 12px 16px;
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: @border-radius;
  text-align: center;

  .account-assign-message {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    color: #1890ff;
    font-size: 14px;

    .van-icon {
      margin-right: 6px;
    }
  }

  .auto-assign-btn {
    background-color: #1989fa;
    border-color: #1989fa;
    color: white;
    padding: 8px 20px;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.3s;

    &:hover {
      background-color: #0c7cd5;
      border-color: #0c7cd5;
    }

    &.van-button--loading {
      background-color: #91d5ff;
      border-color: #91d5ff;
    }
  }
}

/* 移动端适配 - 自动分配账号按钮 */
@media screen and (max-width: 767px) {
  .account-assign-section {
    margin: 12px 0;
    padding: 10px 12px;

    .account-assign-message {
      font-size: 13px;
      margin-bottom: 10px;
    }

    .auto-assign-btn {
      padding: 6px 16px;
      font-size: 13px;
    }
  }
}

/* 登录方式分区样式 */
.login-section {
  margin: 20px 0;
  padding: 16px;
  background-color: #ffffff;
  border-radius: @border-radius;
  border: 1px solid @border-color;
  box-shadow: @box-shadow;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeInContent 1s forwards;
  animation-delay: 0.5s;

  // 登录方式标题
  .section-title {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid @border-color;
    color: @primary-color;
    font-size: 16px;
    font-weight: 500;

    .van-icon {
      margin-right: 8px;
      font-size: 18px;
    }

    .tutorial-link {
      margin-left: auto;
      display: flex;
      align-items: center;
      color: @primary-color;
      text-decoration: none;
      font-size: 14px;
      padding: 4px 8px;
      border-radius: 4px;
      transition: @transition-default;

      &:hover {
        background-color: @primary-light;
      }

      .van-icon {
        font-size: 16px;
        margin-right: 4px;
      }
    }
  }

  // 内部账号密码框样式重置
  .account-password-box {
    margin: 0;
    padding: 0;
    background-color: transparent;
    border: none;
    box-shadow: none;
  }
}

/* 移动端适配 - 登录区域 */
@media screen and (max-width: 767px) {
  .login-section {
    margin: 16px 0;
    padding: 12px;

    .section-title {
      font-size: 15px;
      margin-bottom: 12px;
      padding-bottom: 8px;

      .van-icon {
        font-size: 16px;
      }
    }
  }
}

// 密码项样式
.password-item .value {
  flex: 1;
  color: @text-color;
  font-size: 14px;
  margin-right: 12px;
  letter-spacing: 2px;
}

// 验证码项样式
.verification-code-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #ffffff;
  border-radius: 6px;
  border: 1px solid @border-color;
  margin-top: 8px;

  .label {
    color: @text-secondary;
    font-size: 14px;
    min-width: 50px;
  }

  .value {
    flex: 1;
    margin-right: 12px;
    font-family: monospace;
    letter-spacing: 2px;

    &.has-code {
      color: @primary-color;
      font-size: 16px;
      font-weight: 500;
    }

    &.no-code {
      color: @text-light;
      font-size: 14px;
      font-weight: normal;
    }
  }

  .van-button {
    flex-shrink: 0;
    height: 28px;
    padding: 0 12px;
    min-width: 100px;
  }

  // 移动端适配
  @media screen and (max-width: 767px) {
    padding: 6px 10px;

    .label {
      font-size: 13px;
    }

    .value {
      font-size: 15px;
    }

    .van-button {
      height: 26px;
      padding: 0 10px;
    }
  }
}

// 验证相关组件样式
.verification {
  &-dialog {
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &-image {
    max-width: 100%;
    margin-bottom: 16px;
  }

  &-input {
    width: 100%;
  }

  &-code-input {
    width: 100%;

    :deep(.van-field__control) {
      height: 36px;
      text-align: center;
      font-size: 18px;
      font-family: monospace;
      letter-spacing: 3px;
      background-color: @bg-light;
      border-radius: 4px;
      padding: 0 10px;

      &:focus {
        background-color: @primary-light;
      }
    }
  }
}

// 邮箱验证码弹窗样式
.email-dialog {
  .email-content {
    padding: 16px 0;
  }

  .email-title {
    font-size: 15px;
    color: @text-color;
  }

  .email-code-field {
    padding: 10px 16px;
  }

  .code-value {
    width: 100%;
    text-align: center;
    font-family: monospace;
    font-size: 18px;
    font-weight: 500;
    color: @primary-color;
    letter-spacing: 2px;

    &.clickable {
      cursor: pointer;
      padding: 6px 12px;
      background-color: @primary-light;
      border-radius: 4px;
      transition: all 0.2s;
      user-select: all;

      &:hover {
        background-color: #d0e6ff;
        color: #0056b3;
      }
    }
  }
}

// 模糊效果
.blur-effect {
  filter: blur(8px);
  pointer-events: none;
  user-select: none;
  opacity: 0.8;
  transition: @transition-default;
}

// 标题和头部容器
.title-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.site-header {
  margin-bottom: 20px;
}

// 禁用单独的公告动画
.header-container .custom-announcement,
.header-container .announcement-content,
.header-container .announcement-placeholder,
.header-container .single-announcement,
.header-container .multiple-announcements {
  animation: none;
}

// 全局容器样式
.qr-login-container {
  width: 100%;
  min-height: 100vh;
  background-color: #f7f8fa;
}

// 内容布局
.main-content {
  &-wrapper {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  width: 100%;
  max-width: clamp(350px, 100%, 1050px);
  margin: 0 auto;
  transition: @transition-default;
  box-sizing: border-box;
}

// 响应式布局
@media screen and (min-width: 1200px) {
  .main-content {
    width: 85%;
  }

  .token-info-container {
    padding: 14px 20px;
  }

  .token-info-flex {
    gap: 20px;
  }
}

@media screen and (min-width: 1500px) {
  .main-content {
    width: 75%;
  }

  .token-info-container {
    padding: 16px 24px;
  }

  .token-info-flex {
    gap: 24px;
  }

  .token-action-bar .action-btn {
    padding: 16px 12px;
  }
}

@media screen and (min-width: 2000px) {
  .main-content {
    width: 70%;
  }

  .token-card {
    border-radius: 16px;
  }

  .token-header {
    padding: 20px;
  }

  .token-info-container {
    padding: 20px 30px;
  }

  .token-info-flex {
    gap: 30px;
  }
}

@media screen and (max-width: 767px) {
  .title-container {
    margin-bottom: 15px;
    transition: @transition-default;
  }
}

// 浏览器扫码提示对话框样式
.browser-scan-tip-dialog {
  :deep(.van-dialog__header) {
    padding: 16px 0 10px;
  }

  .custom-dialog-title {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 18px;
    color: @warning-color;

    .van-icon {
      margin-right: 6px;
    }
  }

  .browser-scan-tip-content {
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .tip-message {
      text-align: center;
      margin-bottom: 20px;

      p {
        margin: 8px 0;
        color: @text-secondary;
        font-size: 15px;
        line-height: 1.5;

        &:first-child {
          font-weight: 500;
          color: @warning-color;
        }
      }
    }

    .tip-actions {
      margin-bottom: 10px;

      .van-button {
        padding: 0 16px;
      }
    }
  }
}

// 网页二维码对话框样式
.page-qrcode-dialog {
  :deep(.van-dialog__header) {
    padding: 20px 0 10px;
    font-weight: bold;
    font-size: 18px;
    color: @primary-color;
  }

  .page-qrcode-content {
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .qrcode-container {
      width: 220px;
      height: 220px;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 16px;
      padding: 10px;
      background-color: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .qrcode-tip {
      text-align: center;
      margin-bottom: 10px;

      p {
        margin: 6px 0;
        color: @text-secondary;
        font-size: 14px;
        line-height: 1.4;

        &:first-child {
          color: @primary-color;
          font-weight: 500;
        }
      }
    }
  }
}

// 扫码对话框样式
.scan-dialog {
  :deep(.van-dialog__header) {
    padding: 20px 0;
    font-weight: bold;
    font-size: 18px;
  }

  :deep(.van-dialog__content) {
    padding: 0;
  }

  .scan-container {
    padding: 16px;

    .scan-header {
      font-size: 16px;
      color: #333;
      margin-bottom: 16px;
      text-align: center;
      font-weight: 500;
    }

    .scan-content {
      display: flex;
      flex-direction: column;
      align-items: center;

      .scan-frame {
        position: relative;
        width: 100%;
        height: 300px;
        border: none;
        border-radius: @border-radius + 4;
        overflow: hidden;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

        .scan-canvas {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .scan-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;

          &::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 70%;
            height: 70%;
            transform: translate(-50%, -50%);
            border: 2px solid rgba(25, 137, 250, 0.7);
            border-radius: @border-radius + 4;
            box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.4);
          }

          // 扫描框四角
          .scan-corner {
            position: absolute;
            width: 20px;
            height: 20px;
            border-color: @primary-color;
            border-style: solid;
            border-width: 0;

            &.top-left {
              top: 15%;
              left: 15%;
              border-top-width: 2px;
              border-left-width: 2px;
              border-top-left-radius: @border-radius;
            }

            &.top-right {
              top: 15%;
              right: 15%;
              border-top-width: 2px;
              border-right-width: 2px;
              border-top-right-radius: @border-radius;
            }

            &.bottom-left {
              bottom: 15%;
              left: 15%;
              border-bottom-width: 2px;
              border-left-width: 2px;
              border-bottom-left-radius: @border-radius;
            }

            &.bottom-right {
              bottom: 15%;
              right: 15%;
              border-bottom-width: 2px;
              border-right-width: 2px;
              border-bottom-right-radius: @border-radius;
            }
          }

          // 扫描线
          .scan-line {
            position: absolute;
            top: 15%;
            left: 15%;
            right: 15%;
            height: 2px;
            background: linear-gradient(to right, transparent, @primary-color, transparent);
            animation: scanLine 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          }
        }
      }

      // 扫描提示
      .scan-tip {
        margin-top: 16px;
        font-size: 14px;
        color: #666;
        text-align: center;
        padding: 8px 16px;
        background-color: @bg-light;
        border-radius: 20px;
        box-shadow: @box-shadow;
        animation: pulse 2s infinite;
      }
    }
  }
}

// 扫描动画
@keyframes scanLine {
  0% {
    top: 15%;
  }
  50% {
    top: 85%;
  }
  100% {
    top: 15%;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
  }
}

// 用户名样式
.token-username {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: normal;
  white-space: nowrap;
}

// 密钥输入对话框
.token-input-dialog {
  :deep(.van-dialog__header) {
    padding: 16px 0 5px;
    font-weight: bold;
    font-size: 18px;
  }

  :deep(.van-dialog__content) {
    padding-top: 5px;
  }

  :deep(.van-dialog__footer) {
    padding-top: 0;
    margin-top: -5px;
  }

  .dialog-title-with-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    .dialog-title-icon {
      font-size: 20px;
      color: @primary-color;
      margin-right: 8px;
    }

    span {
      font-size: 18px;
      font-weight: bold;
    }
  }

  .input-wrapper {
    padding: 0 16px;

    .input-field-container {
      height: 40px;
      overflow: hidden;
      display: flex;
      align-items: center;
    }

    .token-input-field {
      margin: 0;

      :deep(.van-cell) {
        padding: 8px 0;
        background-color: transparent;
        min-height: 40px;
        height: 40px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
      }

      :deep(.van-field__control) {
        height: 24px;
        line-height: 24px;
        font-size: 16px;
        letter-spacing: 1px;
        background-color: transparent;
        padding: 0;
        box-sizing: border-box;
        overflow: hidden;
        vertical-align: middle;
      }

      :deep(.van-field__placeholder) {
        line-height: 24px;
        height: 24px;
        vertical-align: middle;
      }

      :deep(.van-field__right-icon) {
        height: 24px;
        line-height: 24px;
      }

      .clear-icon {
        font-size: 16px;
        color: #c8c9cc;
        cursor: pointer;
        padding: 4px;
        box-sizing: border-box;

        &:hover {
          color: #969799;
        }
      }
    }

    .input-underline {
      height: 1px;
      background: linear-gradient(to right, @primary-color, #a0cfff);
      margin: 0 0 8px;
    }

    .input-hint {
      display: flex;
      align-items: center;
      margin-bottom: 6px;

      .van-icon {
        font-size: 12px;
        color: @text-light;
        margin-right: 4px;
      }

      span {
        font-size: 12px;
        color: @text-light;
      }
    }
  }
}


</style>